import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import {
  AreaChart, Area, BarChart, Bar, ComposedChart, LineChart, Line,
  ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, ZAxis
} from "recharts";
import { useWell } from "../WellContext";           
import { useAIAdvice } from "../useAIAdvice";
import { useTheme, getColors } from "../ThemeContext";   // <-- added
import PlotSummary from "../components/PlotSummary";

const MONO = "'Space Mono', monospace";
const SANS = "'Syne', sans-serif";

const C = {
  bg:"#050b14",panel:"#080f1e",card:"#0b1525",border:"#0f2040",border2:"#162a4a",
  cyan:"#00d4ff",teal:"#00ffcc",amber:"#ffb84d",pink:"#ff4da6",purple:"#a855f7",
  blue:"#3b9eff",red:"#ff3d5a",green:"#00ff88",orange:"#ff7c2a",
  text:"#e2eaf8",muted:"#4a6080",muted2:"#2a3f60",
};
function applyTheme(colors) { Object.assign(C, colors); }   // <-- added
/* ───────────── TIME-WINDOW HELPERS (same bar / axis / look as Overview, AI Controller, GLR) ───────────── */
const MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const pad2 = n => String(n).padStart(2, "0");
const HOUR = 3600000, DAY = 86400000;

// backend sends "YYYY-MM-DD HH:MM" - parse as plain local clock time (no timezone shifting)
function parseT(s) {
  if (!s) return null;
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]).getTime() : null;
}
const fmtDT   = ms => { const d = new Date(ms); return `${d.getDate()} ${MON[d.getMonth()]} ${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
const fmtD    = ms => { const d = new Date(ms); return `${d.getDate()} ${MON[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`; };
const fmtHM   = ms => { const d = new Date(ms); return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
const toInput = ms => { const d = new Date(ms); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; };

function fmtSpan(ms) {
  if (ms < HOUR)      return `${Math.max(1, Math.round(ms / 60000))} min`;
  if (ms < 2 * DAY)   return `${Math.round(ms / HOUR)} h`;
  const d = Math.round(ms / DAY);
  return d >= 14 ? `${d} days (${(d / 7).toFixed(1)} wk)` : `${d} days`;
}
function fmtAgo(ms) {
  if (ms < HOUR)    return "just now";
  if (ms < 2 * DAY) return `${Math.round(ms / HOUR)} h ago`;
  return `${Math.round(ms / DAY)} d ago`;
}

const PRESETS = [
  { id: "24h",   label: "24 H",   ms: DAY },
  { id: "7d",    label: "7 D",    ms: 7 * DAY },
  { id: "30d",   label: "30 D",   ms: 30 * DAY },
  { id: "90d",   label: "90 D",   ms: 90 * DAY },
  { id: "cyc30", label: "LAST 30 CYC", cycles: 30 },
  { id: "all",   label: "ALL" },
];
const MIN_POINTS = 3;   // fewer cycles than this cannot draw a meaningful trend

/* Apply a window to ANY array of rows that carry `_t` (ms). Presets are measured back from the LATEST cycle in the data, not from "now". */
function applyWin(w, tMax, arr) {
  if (w.mode === "all" || tMax == null) return arr;
  if (w.mode === "cyc30") {
    if (arr.some(r => r.well != null)) {            // multi-well rows -> newest 30 of EACH well
      const by = {};
      arr.forEach(r => { (by[r.well] = by[r.well] || []).push(r); });
      return Object.values(by).flatMap(a => a.slice(-30));
    }
    return arr.slice(-30);
  }
  if (w.mode === "custom") {
    const f = w.from ? parseT(`${w.from} 00:00`) : -Infinity;
    const t = w.to   ? parseT(`${w.to} 23:59`)   :  Infinity;
    return arr.filter(d => d._t != null && d._t >= f && d._t <= t);
  }
  const p = PRESETS.find(x => x.id === w.mode);
  return arr.filter(d => d._t != null && d._t >= tMax - p.ms);
}
/* `rows` = every cycle time the window bar should know about ({_t, well?}). `filter(arr)` windows any per-well / per-view array the same way. */
function useTimeWindow(rows) {
  const [win, setWin] = useState({ mode: "all", from: "", to: "" });
  const tMin = useMemo(() => { const v = rows.map(d => d._t).filter(t => t != null); return v.length ? Math.min(...v) : null; }, [rows]);
  const tMax = useMemo(() => { const v = rows.map(d => d._t).filter(t => t != null); return v.length ? Math.max(...v) : null; }, [rows]);
  const view = useMemo(() => applyWin(win, tMax, rows), [rows, win, tMax]);
  const presetCounts = useMemo(() => Object.fromEntries(PRESETS.map(p => [p.id, applyWin({ mode: p.id }, tMax, rows).length])), [rows, tMax]);
  return { win, setWin, view, presetCounts, tMin, tMax, isAll: win.mode === "all", filter: arr => applyWin(win, tMax, arr) };
}


/* Second row of the X axis = timestamp of the cycle sitting at that position. */
function DateTick({ x, y, payload, index, lookup, lastIdx, shortSpan }) {
  const t = lookup.get(payload.value);
  if (t == null) return null;
  const anchor = index === 0 ? "start" : index === lastIdx ? "end" : "middle";
  const l1 = shortSpan ? fmtHM(t) : fmtD(t);
  const l2 = shortSpan ? fmtD(t)  : fmtHM(t);
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor={anchor} fill={C.amber} fontSize={9} fontFamily={MONO} dy={11}>{l1}</text>
      <text textAnchor={anchor} fill={C.muted} fontSize={8} fontFamily={MONO} dy={22}>{l2}</text>
    </g>
  );
}

/* Call as a function (NOT <Component/>) - recharts only recognises <XAxis> as a direct child.
   Rows must carry `key` (default "index") and `_t`. labelText = the "Cycle Number →" caption, moved under the timestamp row. */
function dateXAxis(rows, key = "index", nTicks = 5, labelText, opts = {}) {
  const stamped = rows.filter(r => r._t != null);
  if (!stamped.length) return null;
  const last = stamped.length - 1;
  const idx = new Set();
  for (let i = 0; i < nTicks; i++) idx.add(Math.round((i * last) / Math.max(nTicks - 1, 1)));
  const ticks = [...idx].filter(i => stamped[i]).map(i => stamped[i][key]);
  const lookup = new Map(stamped.map(r => [r[key], r._t]));
  const span = stamped.length > 1 ? (stamped[last]._t - stamped[0]._t) : 0;
  return (
    <XAxis xAxisId="date" dataKey={key} ticks={ticks} interval={0} tickLine={false} axisLine={false} height={opts.height || 32}
      tick={<DateTick lookup={lookup} lastIdx={ticks.length - 1} shortSpan={span > 0 && span < 3 * DAY} />}
      label={labelText ? { value: labelText, position: "insideBottom", offset: opts.labelOffset ?? -14, fill: C.muted, fontSize: 9, fontFamily: MONO } : undefined} />
  );
}
const hasStamps = rows => rows.some(r => r._t != null);

function DataWindowBar({ ctl, total, note, isDark }) {
  const { win, setWin, view, presetCounts, tMin, tMax, isAll } = ctl;
  const vs      = view.filter(d => d._t != null);
  const vStart  = vs.length ? Math.min(...vs.map(d => d._t)) : null;
  const vEnd    = vs.length ? Math.max(...vs.map(d => d._t)) : null;
  const hasTime = vStart != null && vEnd != null;
  const spanMs  = hasTime ? Math.max(vEnd - vStart, 0) : 0;
  const tooFew  = view.length < MIN_POINTS;
  const latestAgo = tMax != null ? Date.now() - tMax : null;
  const btn = (active, disabled) => ({
    fontFamily: MONO, fontSize: 10, letterSpacing: 1, padding: "5px 11px", borderRadius: 6, cursor: disabled ? "not-allowed" : "pointer",
    background: active ? `${C.amber}22` : "transparent", color: disabled ? C.muted2 : active ? C.amber : C.muted,
    border: `1px solid ${active ? C.amber : C.border2}`, opacity: disabled ? 0.55 : 1, whiteSpace: "nowrap",
  });
  const dateInput = { fontFamily: MONO, fontSize: 10, background: C.panel, color: C.text, border: `1px solid ${C.border2}`, borderRadius: 6, padding: "4px 8px", colorScheme: isDark ? "dark" : "light" };

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.amber}`, borderRadius: 12, padding: "14px 18px" }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: 2, color: C.muted }}>DATA WINDOW</span>
        <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: C.text }}>
          {hasTime ? `${fmtDT(vStart)}  →  ${fmtDT(vEnd)}` : "no timestamps available"}
        </span>
        {hasTime && <span style={{ fontFamily: MONO, fontSize: 10, color: C.amber, background: `${C.amber}14`, border: `1px solid ${C.amber}33`, borderRadius: 5, padding: "2px 8px" }}>
          {isAll ? "CUMULATIVE" : "WINDOW"} · {fmtSpan(spanMs)}
        </span>}
        <span style={{ fontFamily: MONO, fontSize: 10, color: C.cyan, background: `${C.cyan}12`, border: `1px solid ${C.cyan}33`, borderRadius: 5, padding: "2px 8px" }}>
          {view.length} of {total} cycles
        </span>
        {latestAgo != null && (
          <span title="Presets are measured back from the newest cycle in the data, not from today's date"
            style={{ fontFamily: MONO, fontSize: 10, color: latestAgo > DAY ? C.orange : C.green, background: `${latestAgo > DAY ? C.orange : C.green}12`, border: `1px solid ${latestAgo > DAY ? C.orange : C.green}33`, borderRadius: 5, padding: "2px 8px" }}>
            Latest cycle {fmtDT(tMax)} · {fmtAgo(latestAgo)}
          </span>
        )}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
        {PRESETS.map(p => {
          const n = presetCounts[p.id];
          const disabled = p.id !== "all" && n < MIN_POINTS;
          return (
            <button key={p.id} disabled={disabled} onClick={() => setWin({ mode: p.id, from: "", to: "" })}
              title={disabled ? `Only ${n} cycle${n === 1 ? "" : "s"} in this window, too few to plot a trend. Try a wider window.` : `${n} cycles`}
              style={btn(win.mode === p.id, disabled)}>
              {p.label} <span style={{ opacity: 0.7 }}>· {n}</span>
            </button>
          );
        })}
        <button onClick={() => setWin(w => ({ mode: "custom", from: w.from || (tMin != null ? toInput(tMin) : ""), to: w.to || (tMax != null ? toInput(tMax) : "") }))}
          style={btn(win.mode === "custom", false)}>CUSTOM RANGE</button>
        {win.mode === "custom" && (
          <>
            <input type="date" value={win.from} min={tMin != null ? toInput(tMin) : undefined} max={win.to || (tMax != null ? toInput(tMax) : undefined)}
              onChange={e => setWin(w => ({ ...w, from: e.target.value }))} style={dateInput} />
            <span style={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>to</span>
            <input type="date" value={win.to} min={win.from || (tMin != null ? toInput(tMin) : undefined)} max={tMax != null ? toInput(tMax) : undefined}
              onChange={e => setWin(w => ({ ...w, to: e.target.value }))} style={dateInput} />
          </>
        )}
      </div>
      {tooFew && (
        <div style={{ fontFamily: MONO, fontSize: 10, color: C.orange, marginTop: 8 }}>
          ⚠ Only {view.length} cycle{view.length === 1 ? "" : "s"} in this range, widen the window to see a trend.
        </div>
      )}
    </div>
  );
}


const vals = (rows, k) => rows.map(r => r[k]).filter(v => typeof v === "number" && isFinite(v));
function statsOf(rows, k) {      // same mean / max / median the backend col_stats returns, on the cycles inside the window
  const v = vals(rows, k);
  if (!v.length) return undefined;
  const s = [...v].sort((a, b) => a - b), n = s.length;
  return { mean: v.reduce((a, b) => a + b, 0) / n, max: s[n - 1], min: s[0], median: n % 2 ? s[(n - 1) / 2] : (s[n / 2 - 1] + s[n / 2]) / 2 };
}

const CHART_MARGIN_DEFAULT = { top:8, right:14, left:-10, bottom:36 };
const CHART_MARGIN_SCATTER = { top:8, right:14, left:-10, bottom:36 };

const LEGEND_PROPS = {
  wrapperStyle:{ fontFamily:MONO, fontSize:10, paddingBottom:4 },
  verticalAlign:"top", align:"right", iconSize:8,
};

function cycleTickFmt(v) {
  if (v==null||v==="") return "";
  const n=Number(v); if(!isNaN(n)) return `C${n}`; return String(v);
}
function CYCLE_XAXIS_PROPS() {
  return { tick:{fill:C.muted,fontSize:9,fontFamily:MONO}, tickLine:false, tickFormatter:cycleTickFmt };
}
function plainTickFmt(v) { if(v==null||v==="") return ""; return String(v); }
function PLAIN_XAXIS_PROPS() {
  return { tick:{fill:C.muted,fontSize:9,fontFamily:MONO}, tickLine:false, tickFormatter:plainTickFmt };
}

function AnimNum({ to, dec=1, suffix="" }) {
  if(to==null||isNaN(Number(to))) return <span>-</span>;
  return <span>{Number(to).toFixed(dec)}{suffix}</span>;
}

function StatCard({ label, value, unit, color, sub, icon, delay=0 }) {
  return (
    <div className="card-hover" style={{
      background:C.card, border:`1px solid ${C.border}`, borderTop:`3px solid ${color}`,
      borderRadius:12, padding:"18px 20px",
      animation:`fade-up 0.5s ease ${delay}s both`, position:"relative", overflow:"hidden"
    }}>
      <div style={{ fontSize:18, marginBottom:6 }}>{icon}</div>
      <div style={{ fontFamily:MONO, fontSize:9, color:C.muted, letterSpacing:1.5, textTransform:"uppercase", marginBottom:4 }}>{label}</div>
      <div style={{ fontFamily:MONO, fontSize:24, fontWeight:700, color, lineHeight:1 }}>
        <AnimNum to={value} dec={1} suffix={unit}/>
      </div>
      {sub && <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, marginTop:5 }}>{sub}</div>}
    </div>
  );
}

function ChartCard({ title, subtitle, children, height=260, delay=0, accentColor, summaryTitle, summarySubtitle }) {
  const ac=accentColor||C.cyan;
  return (
    <div style={{
      background:C.card, border:`1px solid ${C.border}`, borderRadius:14,
      padding:"20px 16px 20px", animation:`fade-up 0.5s ease ${delay}s both`,
      position:"relative", overflow:"visible"
    }}>
      <div style={{ position:"absolute", top:0, left:20, right:20, height:2, background:`linear-gradient(to right, transparent, ${ac}88, transparent)` }}/>
      <div style={{ marginBottom:14 }}>
        <div style={{ fontFamily:SANS, fontWeight:700, fontSize:13, color:C.text }}>{title}</div>
        {subtitle && <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, marginTop:2 }}>{subtitle}</div>}
      </div>
      <div style={{ height }}>{children}</div>
      <div style={{ marginTop:10 }}>
        <PlotSummary inline title={summaryTitle||title} subtitle={summarySubtitle||(typeof subtitle==="string"?subtitle:"")} accent={ac}>{children}</PlotSummary>
      </div>
    </div>
  );
}

/* Chart body + field-engineer Summary toggle for charts that sit outside a ChartCard */
// eslint-disable-next-line no-unused-vars
function ChartWithSummary({ height, title, subtitle, accent, children }) {
  return (
    <>
      <div style={{ height }}>{children}</div>
      <PlotSummary inline title={title} subtitle={subtitle} accent={accent}>{children}</PlotSummary>
    </>
  );
}

/* ───────────── BUILDUP PLATEAU & SHUT-IN OPPORTUNITY (estimated from cycle records) ───────────── */
const SI_VERDICT = {
  EXCESS_SHUTIN:         { label:"EXCESS SHUT-IN",        color:"orange", action:"Open earlier" },
  PLATEAU_UNPROVEN:      { label:"PLATEAU - LIFT UNPROVEN", color:"amber",  action:"Investigate - do not shorten" },
  PLATEAU_AT_FLOOR:      { label:"PLATEAU AT FLOOR",      color:"cyan",   action:"Nothing to trim" },
  BUILDING:              { label:"STILL BUILDING",        color:"green",  action:"Shut-in is productive" },
  NO_MEASURABLE_BUILDUP: { label:"NO MEASURABLE BUILDUP", color:"muted",  action:"Not enough signal" },
};
const SEV_COLOR = { HIGH:"red", MEDIUM:"orange", LOW:"amber", GOOD:"green", INFO:"muted" };

function Tag({ color, children, title }) {
  return <span title={title} style={{ fontFamily:MONO, fontSize:10, letterSpacing:1, color, background:`${color}14`, border:`1px solid ${color}33`, borderRadius:5, padding:"2px 8px", whiteSpace:"nowrap", justifySelf:"start" }}>{children}</span>;
}
function KV({ k, v, color }) {
  return (
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", gap:12, padding:"6px 0", borderBottom:`1px solid ${C.border}` }}>
      <span style={{ fontFamily:MONO, fontSize:10, color:C.muted }}>{k}</span>
      <span style={{ fontFamily:MONO, fontSize:11, fontWeight:700, color:color||C.text, textAlign:"right" }}>{v}</span>
    </div>
  );
}
function MiniStat({ label, value, sub, color }) {
  return (
    <div style={{ background:`${color}0d`, border:`1px solid ${color}33`, borderRadius:10, padding:"12px 14px" }}>
      <div style={{ fontFamily:MONO, fontSize:9, color:C.muted, letterSpacing:1.5, textTransform:"uppercase", marginBottom:4 }}>{label}</div>
      <div style={{ fontFamily:MONO, fontSize:20, fontWeight:700, color, lineHeight:1.1 }}>{value}</div>
      {sub && <div style={{ fontFamily:MONO, fontSize:9, color:C.muted, marginTop:4 }}>{sub}</div>}
    </div>
  );
}
const f0 = v => (v==null||!isFinite(v)) ? "-" : Number(v).toFixed(0);
const f1 = v => (v==null||!isFinite(v)) ? "-" : Number(v).toFixed(1);

/* ───────────── HOVER AI RECOMMENDATION FOR OPPORTUNITY ROWS ───────────── */
// eslint-disable-next-line no-unused-vars
function OppRow({ field, value, unit, cycle, context, labels, style, children }) {
  const { advice, loading, fetchAdvice, clearAdvice } = useAIAdvice();
  const [pos, setPos]     = useState(null);
  const [asked, setAsked] = useState(false);
  const timer = useRef(null);
  const ctx = Object.fromEntries(Object.entries(context).filter(([,v])=>typeof v==="number"&&isFinite(v)));
  const fields = Object.entries(ctx).filter(([k])=>labels[k]).map(([k,v])=>({ field:k, label:labels[k][0], value:Math.round(v*100)/100, unit:labels[k][1] }));
  useEffect(()=>()=>clearTimeout(timer.current),[]);
  function onEnter() {
    setAsked(false);
    clearTimeout(timer.current);
    timer.current = setTimeout(()=>{ setAsked(true); fetchAdvice(field, value, unit, cycle, ctx, fields); }, 300);
  }
  function onMove(e) {
    const TW=330, TH=200;
    const x=e.clientX+18, y=e.clientY+14;
    setPos({ x:x+TW>window.innerWidth?e.clientX-TW-18:x, y:y+TH>window.innerHeight?window.innerHeight-TH-16:y });
  }
  function onLeave() { clearTimeout(timer.current); setPos(null); setAsked(false); clearAdvice(); }
  const ac = !advice ? C.cyan : advice.startsWith("■")||advice.startsWith("▽") ? C.red : advice.startsWith("▲")||advice.startsWith("▶") ? C.amber : C.green;
  const text = advice ? advice.replace(/^[◆▲▶■▽]\s*/, "") : "";
  return (
    <div style={style} onMouseEnter={onEnter} onMouseMove={onMove} onMouseLeave={onLeave}>
      {children}
      {pos && createPortal(
        <div style={{ position:"fixed", left:pos.x, top:pos.y, zIndex:999999, pointerEvents:"none", width:320, background:C.card, border:`1px solid ${ac}44`, borderLeft:`4px solid ${ac}`, borderRadius:12, padding:"12px 16px", fontFamily:MONO, fontSize:10, lineHeight:1.6, boxShadow:"0 12px 40px rgba(0,0,0,0.8)" }}>
          <div style={{ color:C.cyan, fontWeight:700, fontSize:12, marginBottom:6 }}>Cycle {cycle}</div>
          {(!asked||loading) ? <span style={{ color:C.muted }}>Getting AI recommendation...</span>
            : advice ? <span style={{ color:ac }}>{text}</span>
            : <span style={{ color:C.muted }}>Hover a row for AI advice</span>}
        </div>, document.body)}
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
const SI_OPP_LABELS = {
  suggested_shutin_lo: ["Suggested Shut-In Duration (shortest)", "minutes"],
  suggested_shutin_hi: ["Suggested Shut-In Duration (conservative)", "minutes"],
  saved_min:      ["Recoverable Shut-In (upper)", "minutes"],
  saved_min_lo:   ["Recoverable Shut-In (lower)", "minutes"],
  plateau_min:    ["Plateau Time", "minutes"],
  effective_min:  ["Useful Buildup", "minutes"],
  shutin_min:     ["Shut-In Duration", "minutes"],
  pc_open:        ["Casing Pressure @ Open", "psi"],
  gain_psi:       ["Casing Gain This Shut-In", "psi"],
  ref_slope:      ["Normal Buildup Slope", "psi/min"],
  slope_pct:      ["Slope Remaining vs Normal", "%"],
  min_fall_min:   ["Plunger Fall-Time Floor", "minutes"],
  lift_margin_psi:["Lift Margin vs Proven Minimum", "psi"],
  confidence_pct: ["Estimate Confidence", "%"],
};

/* Expandable row for the shut-in opportunities list (click to show / hide the factors behind the estimate) */
function OppItem({ r, v, vc, sc }) {
  const [open, setOpen] = useState(false);
  const isEx = r.verdict==="EXCESS_SHUTIN";
  // Suggested shut-in DURATION (minutes): shortest (aggressive) .. conservative first step. Backend sends it; fall back to shutin - saved.
  const minLo = isEx ? (fin(r.suggested_shutin_lo) ? r.suggested_shutin_lo : (fin(r.shutin_min) && fin(r.saved_min) ? Math.max(0, r.shutin_min - r.saved_min) : null)) : null;
  const minHi = isEx ? (fin(r.suggested_shutin_hi) ? r.suggested_shutin_hi : (fin(r.shutin_min) && fin(r.saved_min_lo) ? Math.max(0, r.shutin_min - r.saved_min_lo) : null)) : null;
  const facts = [
    isEx && fin(minLo) && fin(minHi) && ["Suggested shut-in duration (shortest to conservative)", `${f0(minLo)}-${f0(minHi)} minutes`],
    ["Shut-in duration (actual)", fin(r.shutin_min)?`${f1(r.shutin_min)} minutes`:"-"],
    isEx && ["Time that can be saved (range)", `${f0(r.saved_min_lo)}-${f0(r.saved_min)} minutes`],
    ["Plateau time (little pressure added)", fin(r.plateau_min)?`${f1(r.plateau_min)} minutes`:"-"],
    ["Useful buildup time", fin(r.effective_min)?`${f1(r.effective_min)} minutes`:"-"],
    ["Plunger fall-time floor", fin(r.min_fall_min)?`${f1(r.min_fall_min)} minutes`:"-"],
    ["Casing pressure at open", fin(r.pc_open)?`${f1(r.pc_open)} psi`:"-"],
    ["Casing gain this shut-in", fin(r.gain_psi)?`${r.gain_psi.toFixed(1)} psi`:"-"],
    ["Well's normal buildup slope", fin(r.ref_slope)?`${r.ref_slope.toFixed(3)} psi/min`:"-"],
    ["Slope remaining vs normal", fin(r.slope_ratio)?`${(r.slope_ratio*100).toFixed(0)}%`:"-"],
    ["Lift margin vs proven minimum", fin(r.lift_margin_psi)?`${r.lift_margin_psi>0?"+":""}${r.lift_margin_psi.toFixed(1)} psi`:"unproven"],
    ["Estimate confidence", fin(r.confidence)?`${(r.confidence*100).toFixed(0)}%`:"-"],
  ].filter(Boolean);
  return (
    <div style={{ borderTop:`1px solid ${C.border}` }}>
      <div onClick={()=>setOpen(o=>!o)} style={{ display:"grid", gridTemplateColumns:"minmax(120px,1.1fr) minmax(120px,1fr) 150px 70px 1.6fr 24px", gap:10, alignItems:"center", padding:"8px 0", cursor:"pointer" }}>
        <span style={{ fontFamily:MONO, fontSize:10, color:C.amber }}>{r._t!=null?fmtDT(r._t):"-"} · C{r.index}</span>
        <Tag color={vc}>{v.label}</Tag>
        <span style={{ fontFamily:MONO, fontSize:11, fontWeight:700, color:vc }}>{isEx&&fin(minLo)&&fin(minHi)?`${f0(minLo)}-${f0(minHi)} minutes`:"No change suggested"}</span>
        <Tag color={sc}>{r.severity}</Tag>
        <span style={{ fontFamily:MONO, fontSize:9, color:C.muted }}>conf {(r.confidence*100).toFixed(0)}% · actual shut-in {f0(r.shutin_min)} minutes</span>
        <span style={{ fontFamily:MONO, fontSize:11, color:C.muted, textAlign:"right" }}>{open?"▲":"▼"}</span>
      </div>
      {open && (
        <div style={{ background:`${vc}0d`, border:`1px solid ${vc}33`, borderLeft:`3px solid ${vc}`, borderRadius:8, padding:"10px 14px", marginBottom:10 }}>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(230px, 1fr))", gap:"4px 24px" }}>
            {facts.map(([k,val])=>(
              <div key={k} style={{ display:"flex", justifyContent:"space-between", gap:10, fontFamily:MONO, fontSize:10, padding:"3px 0", borderBottom:`1px solid ${C.border}` }}>
                <span style={{ color:C.muted }}>{k}</span><span style={{ color:C.text, fontWeight:700 }}>{val}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BuildupOpportunityPanel({ rows, basis, TTip }) {
  if(!rows.length) return null;
  const scored  = rows.filter(r=>r.effective_min!=null);
  const sum     = k => scored.reduce((a,r)=>a+(r[k]||0),0);
  const eff = sum("effective_min"), plat = sum("plateau_min"), tot = eff+plat;
  const excess  = rows.filter(r=>r.verdict==="EXCESS_SHUTIN");
  const recov   = excess.reduce((a,r)=>a+(r.saved_min||0),0);
  const belowFall = rows.filter(r=>r.below_fall).length;
  const latest  = rows.filter(r=>r.verdict!=="NO_DATA").sort((a,b)=>((b._t??-1)-(a._t??-1))||(b.index-a.index))[0];
  const topOpp  = rows.filter(r=>r.verdict==="EXCESS_SHUTIN"||r.verdict==="PLATEAU_UNPROVEN").sort((a,b)=>(b.saved_min||0)-(a.saved_min||0)||(b.plateau_min||0)-(a.plateau_min||0)).slice(0,5);
  const hasT    = hasStamps(scored);
  const cm      = hasT ? { ...CHART_MARGIN_DEFAULT, bottom:22 } : CHART_MARGIN_DEFAULT;
  const lv      = latest ? (SI_VERDICT[latest.verdict]||{ label:latest.verdict, color:"muted", action:"" }) : null;
  const lc      = lv ? C[lv.color] : C.muted;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderLeft:`3px solid ${C.orange}`, borderRadius:12, padding:"14px 18px", animation:"fade-up 0.5s ease 0.12s both" }}>
        <div style={{ display:"flex", flexWrap:"wrap", alignItems:"center", gap:10 }}>
          <span style={{ fontFamily:SANS, fontWeight:700, fontSize:14, color:C.text }}>Buildup Plateau &amp; Shut-In Opportunity</span>
          <Tag color={C.amber} title={basis||""}>ESTIMATED FROM CYCLE RECORDS</Tag>
          <Tag color={C.cyan}>{scored.length} of {rows.length} cycles scored</Tag>
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(190px, 1fr))", gap:12 }}>
        <MiniStat label="Useful buildup" value={`${f0(eff)} minutes`} sub={tot>0?`${(eff/tot*100).toFixed(0)}% of scored shut-in time`:""} color={C.teal}/>
        <MiniStat label="Plateau time" value={`${f0(plat)} minutes`} sub={tot>0?`${(plat/tot*100).toFixed(0)}% of scored shut-in time`:""} color={C.amber}/>
        <MiniStat label="Recoverable shut-in" value={`${f0(recov)} minutes`} sub={`${excess.length} cycle${excess.length===1?"":"s"} with a safe early-open`} color={excess.length?C.orange:C.green}/>
        <MiniStat label="Below plunger fall time" value={`${belowFall} of ${rows.length}`} sub="drop not confirmed - extend, don't trim" color={belowFall?C.blue:C.green}/>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"minmax(300px, 1fr) 2fr", gap:20 }}>
        {latest && (
          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderTop:`3px solid ${lc}`, borderRadius:14, padding:"18px 18px", animation:"fade-up 0.5s ease 0.15s both" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:8, marginBottom:10 }}>
              <span style={{ fontFamily:MONO, fontSize:9, letterSpacing:2, color:C.muted }}>LATEST CYCLE IN WINDOW</span>
              <span style={{ fontFamily:MONO, fontSize:10, color:C.amber }}>{latest._t!=null?fmtDT(latest._t):`Cycle ${latest.index}`}</span>
            </div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:8, alignItems:"center", marginBottom:10 }}>
              <Tag color={lc}>{lv.label}</Tag>
              <Tag color={C.muted} title="Confidence in this estimate: history depth, how clear the buildup is, and lift evidence">CONF {(latest.confidence*100).toFixed(0)}%</Tag>
              {latest.below_fall && <Tag color={C.blue}>BELOW FALL TIME</Tag>}
            </div>
            <div style={{ fontFamily:SANS, fontWeight:700, fontSize:15, color:lc, marginBottom:8 }}>{lv.action}{latest.saved_min>0 && latest.verdict==="EXCESS_SHUTIN" ? ` ~${f0(latest.saved_min_lo)}-${f0(latest.saved_min)} minutes sooner` : ""}</div>
            <KV k={"Shut-in (cycle #"+latest.index+")"} v={`${f1(latest.shutin_min)} minutes`}/>
            <KV k="Casing at open" v={`${f1(latest.pc_open)} psi`}/>
            <KV k="Casing gain this shut-in" v={`${latest.gain_psi!=null?latest.gain_psi.toFixed(1):"-"} psi`}/>
            <KV k="Well's normal buildup slope" v={latest.ref_slope!=null?`${latest.ref_slope.toFixed(3)} psi/min`:"-"}/>
            <KV k="Slope remaining (avg vs normal)" v={latest.slope_ratio!=null?`${(latest.slope_ratio*100).toFixed(0)}%`:"-"} color={latest.slope_ratio!=null&&latest.slope_ratio<0.3?C.orange:C.text}/>
            <KV k="Useful buildup / plateau" v={latest.effective_min!=null?`${f1(latest.effective_min)} / ${f1(latest.plateau_min)} minutes`:"-"}/>
            <KV k="Plunger fall-time floor" v={latest.min_fall_min!=null?`${f1(latest.min_fall_min)} minutes`:"-"}/>
            <KV k="Lift margin vs proven minimum" v={latest.lift_margin_psi!=null?`${latest.lift_margin_psi>0?"+":""}${latest.lift_margin_psi.toFixed(1)} psi`:"unproven"} color={latest.lift_ok===false?C.red:latest.lift_ok?C.green:C.muted}/>
            <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, lineHeight:1.7, marginTop:10, background:`${lc}0d`, borderRadius:8, padding:"8px 10px" }}>{latest.explanation}</div>
          </div>
        )}

        <ChartCard title="Shut-In Time Allocation: Productive Buildup vs Plateau"
          subtitle="minutes of each shut-in - plateau = time that added little casing pressure (estimated)"
          summarySubtitle="minutes of each shut-in split into useful buildup and plateau time that added little pressure (estimated from cycle records)"
          accentColor={C.orange} height={300} delay={0.18}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={scored} margin={cm}>
              <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
              <XAxis dataKey="index" {...CYCLE_XAXIS_PROPS()} height={hasT?20:undefined} label={hasT?undefined:{ value:"Cycle", position:"insideBottom", offset:-18, fill:C.muted, fontSize:9, fontFamily:MONO }}/>
              {dateXAxis(scored,"index",5,"Cycle")}
              <YAxis tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false} label={{ value:"minutes", angle:-90, position:"insideLeft", offset:14, fill:C.muted, fontSize:9, fontFamily:MONO }}/>
              <Tooltip content={<TTip />} wrapperStyle={{ zIndex:99999, overflow:"visible !important" }}/>
              <Legend {...LEGEND_PROPS}/>
              <Bar dataKey="effective_min" name="Useful buildup (minutes)" fill={C.teal}  fillOpacity={0.85} radius={[3,3,0,0]}/>
              <Bar dataKey="plateau_min"   name="Plateau (minutes)" fill={C.amber} fillOpacity={0.85} radius={[3,3,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {topOpp.length>0 && (
        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:"16px 18px", animation:"fade-up 0.5s ease 0.2s both" }}>
          <div style={{ fontFamily:SANS, fontWeight:700, fontSize:13, color:C.text, marginBottom:4 }}>Top Shut-In Reduction Opportunities (Selected Window)</div>
          <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, marginBottom:10 }}>Only cycles where the plateau lasted at least 3 minutes. "Lift unproven" cycles are listed for review, not for shortening.</div>
          <div style={{ display:"grid", gridTemplateColumns:"minmax(120px,1.1fr) minmax(120px,1fr) 150px 70px 1.6fr 24px", gap:10, padding:"6px 0", fontFamily:MONO, fontSize:9, letterSpacing:1, color:C.muted }}>
            <span>CYCLE</span><span>VERDICT</span><span title="Suggested shut-in duration to open at, shown as shortest (aggressive) to conservative first step, in minutes. Rows with no change suggested are for review only.">SUGGESTED SHUT-IN (MINUTES)</span><span>SEVERITY</span><span>CONFIDENCE · ACTUAL SHUT-IN</span><span/>
          </div>
          {topOpp.map(r=>{
            const v = SI_VERDICT[r.verdict]; const vc = C[v.color]; const sc = C[SEV_COLOR[r.severity]||"muted"];
            return <OppItem key={r.index} r={r} v={v} vc={vc} sc={sc}/>;
          })}
        </div>
      )}
    </div>
  );
}

/* ───────────── BAR-CHART VERSIONS: Pressure Buildup Rate Over Time  /  Buildup Rate vs Shut-In Duration ───────────── */
const fin = v => typeof v==="number" && isFinite(v);
const f2  = v => (v==null||!isFinite(v)) ? "-" : Number(v).toFixed(2);
const f3  = v => (v==null||!isFinite(v)) ? "-" : Number(v).toFixed(3);

function ToggleBtns({ options, value, onChange, color }) {
  return (
    <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:12 }}>
      {options.map(o=>{
        const active = value===o.id;
        return (
          <button key={o.id} onClick={()=>onChange(o.id)} style={{
            fontFamily:MONO, fontSize:10, letterSpacing:1, padding:"5px 11px", borderRadius:6, cursor:"pointer",
            background:active?`${color}22`:"transparent", color:active?color:C.muted,
            border:`1px solid ${active?color:C.border2}`, whiteSpace:"nowrap",
          }}>{o.label}</button>
        );
      })}
    </div>
  );
}

/* helpers for the duration chart insight */
const pearson = (xs, ys) => {
  const n = xs.length; if (n < 3) return null;
  const mx = xs.reduce((a,b)=>a+b,0)/n, my = ys.reduce((a,b)=>a+b,0)/n;
  let sxy=0, sxx=0, syy=0;
  for (let i=0;i<n;i++){ const dx=xs[i]-mx, dy=ys[i]-my; sxy+=dx*dy; sxx+=dx*dx; syy+=dy*dy; }
  return sxx>0 && syy>0 ? sxy/Math.sqrt(sxx*syy) : null;
};
const medianOf = arr => {
  const s = arr.filter(v=>typeof v==="number"&&isFinite(v)).sort((a,b)=>a-b), n = s.length;
  return n ? (n%2 ? s[(n-1)/2] : (s[n/2-1]+s[n/2])/2) : null;
};

function LegendItem({ color, text, dashed }) {
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:6, fontFamily:MONO, fontSize:10, color:C.muted }}>
      {dashed
        ? <span style={{ width:16, height:0, borderTop:`2px dashed ${color}` }}/>
        : <span style={{ width:10, height:10, borderRadius:2, background:color, opacity:0.85 }}/>}
      {text}
    </span>
  );
}

/* Hover tooltip for the combined plot: same look / same hover AI recommendation as every other chart on the page.
   Works for both views - one cycle (cycle # + time) or one shut-in-duration bucket. */
/* [3,4,5,9,12,13] -> "C3: C5, C9, C12: C13" (long lists are cut with "+N more") */
function fmtCycleList(ids, maxParts=14) {
  if(!ids || !ids.length) return "";
  const parts = []; let a = ids[0], b = ids[0], cnt = 1;
  for(let i=1;i<=ids.length;i++){
    if(i<ids.length && ids[i]===b+1){ b = ids[i]; cnt++; continue; }
    parts.push({ txt: a===b ? `C${a}` : b===a+1 ? `C${a}, C${b}` : `C${a}: C${b}`, cnt });
    a = b = ids[i]; cnt = 1;
  }
  if(parts.length<=maxParts) return parts.map(x=>x.txt).join(", ");
  const shown = parts.slice(0,maxParts);
  const left = ids.length - shown.reduce((t,x)=>t+x.cnt,0);
  return `${shown.map(x=>x.txt).join(", ")} … +${left} more`;
}
function ComboTip({ active, payload, mode, metricKey, metricUnit, windowAvgRate }) {
  const { advice, loading, fetchAdvice, clearAdvice } = useAIAdvice();
  const prevKey = useRef(null);
  const [pos, setPos] = useState({ x:0, y:0 });
  const row = active && payload?.length ? (payload[0].payload || {}) : null;
  const isCycle = mode==="cycle";

  const req = (() => {
    if(!row) return null;
    if(isCycle) {
      const v = row[metricKey];
      if(!fin(v)) return null;
      const fields = [
        { field:"TimeOffTotal", label:"Shut-In Duration", value:r2(row.TimeOffTotal), unit:"min" },
        { field:"BuildupRate_psi_per_min", label:"Pressure Buildup Rate", value:r2(row.BuildupRate_psi_per_min), unit:"psi/min" },
        fin(row.BuildupGain_psi) && { field:"BuildupGain_psi", label:"Total Casing Buildup", value:r2(row.BuildupGain_psi), unit:"psi" },
        fin(row.CsgAtOpen)  && { field:"CsgAtOpen",  label:"Casing Pressure @ Open",  value:r2(row.CsgAtOpen),  unit:"psi" },
        fin(row.CsgAtClose) && { field:"CsgAtClose", label:"Casing Pressure @ Close", value:r2(row.CsgAtClose), unit:"psi" },
        fin(row.LiquidHeight_ft) && { field:"LiquidHeight_ft", label:"Liquid Column Height", value:r2(row.LiquidHeight_ft), unit:"ft" },
      ].filter(Boolean);
      return { field:metricKey, value:v, unit:metricUnit, cycle:row.index, fields };
    }
    if(!fin(row.avg_rate)) return null;
    const fields = [
      { field:"bucket_range",   label:"Shut-In Duration Bucket", value:row.bucket, unit:"min" },
      { field:"cycles",         label:"Cycles in this bucket",   value:row.cycles, unit:"cycles" },
      row.cycle_ids?.length && { field:"cycle_ids", label:"Cycle numbers in bucket", value:fmtCycleList(row.cycle_ids, 8), unit:"" },
      { field:"BuildupRate_psi_per_min", label:"Average Buildup Rate in bucket", value:r2(row.avg_rate), unit:"psi/min" },
      fin(windowAvgRate) && { field:"window_avg_rate", label:"Window average buildup rate", value:r2(windowAvgRate), unit:"psi/min" },
      fin(row.avg_gain) && { field:"avg_gain", label:"Average casing buildup in bucket", value:r2(row.avg_gain), unit:"psi" },
    ].filter(Boolean);
    return { field:"BuildupRate_psi_per_min", value:row.avg_rate, unit:"psi/min", cycle:`bucket ${row.bucket} min`, fields };
  })();

  const reqKey = req ? `${req.field}_${r2(req.value)}_${req.cycle}` : null;
  useEffect(() => {
    if(!active || !req) return;
    if(prevKey.current===reqKey) return;
    const timer = setTimeout(() => {
      prevKey.current = reqKey;
      const ctx = {};
      req.fields.forEach(f => { if(typeof f.value==="number") ctx[f.field] = f.value; });
      fetchAdvice(req.field, req.value, req.unit, req.cycle, ctx, req.fields);
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, reqKey]);
  useEffect(() => { if(!active) { clearAdvice(); prevKey.current = null; } }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function onMove(e) {
      const TW=330, TH=430;
      const x=e.clientX+18, y=e.clientY-10;
      setPos({ x:x+TW>window.innerWidth?e.clientX-TW-18:x, y:y+TH>window.innerHeight?window.innerHeight-TH-16:y });
    }
    if(active) window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [active]);

  if(!active || !payload?.length || !row) return null;

  const ac = !advice ? C.cyan : advice.startsWith("■")||advice.startsWith("▽") ? C.red : advice.startsWith("▲")||advice.startsWith("▶") ? C.amber : C.green;
  const adviceText = advice ? advice.replace(/^[◆▲▶■▽]\s*/, "") : "";
  const shown = payload.filter(p => p.value!=null && p.dataKey!=="_zero");
  return createPortal(
    <div style={{ position:"fixed", left:pos.x, top:pos.y, zIndex:999999, pointerEvents:"none" }}>
      <div style={{ background:C.card, border:`1px solid ${C.border2}`, borderRadius:12, padding:"12px 16px", fontFamily:MONO, fontSize:11, boxShadow:"0 12px 40px rgba(0,0,0,0.8)", maxWidth:320, maxHeight:420, overflowY:"auto" }}>
        <div style={{ borderBottom:`1px solid ${C.border}`, paddingBottom:7, marginBottom:8 }}>
          {isCycle
            ? <div style={{ color:C.cyan, fontWeight:700, fontSize:12, marginBottom:2 }}>Cycle {row.index}</div>
            : <div style={{ color:C.cyan, fontWeight:700, fontSize:12, marginBottom:2 }}>Shut-in {row.bucket} min</div>}
          {isCycle && row.time && <div style={{ color:C.muted, fontSize:9 }}>{row.time}</div>}
          {!isCycle && <div style={{ color:C.muted, fontSize:9 }}>{row.cycles} cycle{row.cycles===1?"":"s"} fall in this duration range</div>}
          {!isCycle && row.cycle_ids?.length>0 && <div style={{ color:C.amber, fontSize:10, marginTop:4, lineHeight:1.5, wordBreak:"break-word" }}>Cycles: {fmtCycleList(row.cycle_ids)}</div>}
        </div>
        {shown.map((p,i)=>(
          <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12, marginBottom:4, paddingBottom:4, borderBottom:i<shown.length-1?`1px solid ${C.border}`:"none" }}>
            <div style={{ color:C.muted, fontSize:10, flex:1, lineHeight:1.3 }}>{p.name}</div>
            <div style={{ textAlign:"right", flexShrink:0 }}>
              <span style={{ color:p.color||C.cyan, fontWeight:700, fontSize:12 }}>{typeof p.value==="number"?(Math.abs(p.value)>=100?p.value.toFixed(1):p.value.toFixed(3)):String(p.value)}</span>
              {p.unit && <span style={{ color:C.muted, fontSize:9, marginLeft:3 }}>{p.unit}</span>}
            </div>
          </div>
        ))}
        <div style={{ marginTop:8, paddingTop:8, borderTop:`1px solid ${C.border}`, fontSize:10, lineHeight:1.6, background:loading?`${C.muted}08`:`${ac}10`, borderLeft:advice?`3px solid ${ac}`:"none", borderRadius:6, padding:"6px 8px" }}>
          {loading ? <span style={{ color:C.muted }}>Getting AI recommendation...</span>
            : advice ? <span style={{ color:ac }}>{adviceText}</span>
            : <span style={{ color:C.muted }}>Hover a data point for AI advice</span>}
        </div>
      </div>
    </div>, document.body);
}
const r2 = v => (typeof v==="number" && isFinite(v)) ? Math.round(v*100)/100 : v;

/* ONE combined plot: bars + line on two Y axes, so shut-in and buildup are read together.
   cycle view  : X = cycle (with timestamp row) | bars = shut-in duration (min) | line = buildup rate (psi/min) or total casing buildup (psi)
   bucket view : X = shut-in duration bucket    | bars = cycles in bucket       | line = average buildup rate (psi/min)
   Red dots = cycles whose casing gained nothing during the shut-in. Dashed lines = window averages. */
function DurationRateChart({ data, mode, hasT, metricKey, metricName, metricUnit, barKey, barName, barColor, lineColor, barAvg, barAvgLabel, lineAvg, lineAvgLabel, windowAvgRate }) {
  const tick = { fill:C.muted, fontSize:9, fontFamily:MONO };
  const isCycle = mode==="cycle";
  const lineKey = isCycle ? metricKey : "avg_rate";
  const xTitle = { value:isCycle?"Cycle":"Shut-in duration bucket (min)", position:"insideBottom", offset:-18, fill:C.muted, fontSize:9, fontFamily:MONO };
  const lineDot = ({ cx, cy, payload }) => {
    if(cx==null || cy==null) return null;
    const bad = isCycle ? (fin(payload.BuildupRate_psi_per_min) && payload.BuildupRate_psi_per_min<=0) : false;
    return <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={isCycle?3:4.5} fill={bad?C.red:lineColor} stroke={C.card} strokeWidth={1}/>;
  };
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={data} margin={{ top:14, right:6, left:-10, bottom:isCycle&&hasT?22:36 }}>
        <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
        {isCycle
          ? <XAxis dataKey="index" {...CYCLE_XAXIS_PROPS()} height={hasT?20:undefined} label={hasT?undefined:xTitle}/>
          : <XAxis dataKey="bucket" tick={tick} tickLine={false} label={xTitle}/>}
        {isCycle && dateXAxis(data,"index",5,"Cycle")}
        <YAxis yAxisId="L" width={60} tick={{ ...tick, fill:barColor }} tickLine={false} axisLine={false} allowDecimals={isCycle}
          label={{ value:isCycle?"Shut-in duration (min)":"Number of cycles", angle:-90, position:"insideLeft", offset:14, fill:barColor, fontSize:9, fontFamily:MONO }}/>
        <YAxis yAxisId="R" orientation="right" width={56} tick={{ ...tick, fill:lineColor }} tickLine={false} axisLine={false}
          label={{ value:isCycle?`${metricName} (${metricUnit})`:"Avg buildup rate (psi/min)", angle:90, position:"insideRight", offset:10, fill:lineColor, fontSize:9, fontFamily:MONO }}/>
        <Tooltip content={<ComboTip mode={mode} metricKey={metricKey} metricUnit={metricUnit} windowAvgRate={windowAvgRate}/>} cursor={{ fill:`${barColor}14` }} wrapperStyle={{ zIndex:99999, overflow:"visible !important" }}/>
        {barAvg!=null && isCycle && <ReferenceLine yAxisId="L" y={barAvg} stroke={barColor} strokeWidth={1} strokeDasharray="5 3" label={{ value:barAvgLabel, fill:barColor, fontSize:9, fontFamily:MONO, position:"insideTopLeft" }}/>}
        {lineAvg!=null && <ReferenceLine yAxisId="R" y={lineAvg} stroke={C.amber} strokeWidth={1.5} strokeDasharray="5 3" label={{ value:lineAvgLabel, fill:C.amber, fontSize:9, fontFamily:MONO, position:"insideTopRight" }}/>}
        <Bar yAxisId="L" dataKey={barKey} name={barName} unit={isCycle?" min":" cycles"} fill={barColor} fillOpacity={0.55} radius={[3,3,0,0]} maxBarSize={isCycle?14:48}/>
        <Line yAxisId="R" type="monotone" dataKey={lineKey} name={isCycle?metricName:"Avg Buildup Rate"} unit={isCycle?` ${metricUnit}`:" psi/min"} stroke={lineColor} strokeWidth={2} connectNulls dot={lineDot} activeDot={{ r:5, stroke:lineColor, strokeWidth:2 }}/>
      </ComposedChart>
    </ResponsiveContainer>
  );
}

/* Buildup Rate vs Shut-In Duration.
   Header = averages + legend + plain-English reading (is a longer shut-in actually buying a faster buildup?).
   PER CYCLE   : ONE plot, cycle on the X axis - shut-in duration (bars) and buildup (line) together.
   BY DURATION : ONE plot, duration bucket on the X axis - cycles per bucket (bars) and average buildup rate (line) together. */
function BuildupVsDurationChart({ rows, hasT }) {
  const [mode, setMode]   = useState("cycle");
  const [bmode, setBmode] = useState("rate");     // rate (psi/min) or total casing buildup (psi)
  const pts = rows.filter(d=>fin(d.BuildupRate_psi_per_min)&&fin(d.TimeOffTotal)&&d.TimeOffTotal>0)
    .map(d=>({ ...d, BuildupGain_psi: fin(d.CsgAtOpen)&&fin(d.CsgAtClose) ? Math.round((d.CsgAtOpen-d.CsgAtClose)*100)/100 : null }));
  const buckets = (() => {
    if(!pts.length) return [];
    const d = pts.map(p=>p.TimeOffTotal);
    const mn = Math.min(...d), mx = Math.max(...d);
    const nb = Math.min(8, Math.max(1, Math.ceil(Math.sqrt(pts.length))));
    const w  = (mx-mn)/nb || 1;
    const bins = Array.from({ length:nb }, (_,i)=>({ lo:mn+i*w, hi:mn+(i+1)*w, n:0, sum:0, gsum:0, gn:0, ids:[] }));
    pts.forEach(p=>{ const b = bins[Math.min(nb-1, Math.floor((p.TimeOffTotal-mn)/w))]; b.n++; b.ids.push(p.index); b.sum += p.BuildupRate_psi_per_min; if(fin(p.BuildupGain_psi)){ b.gsum += p.BuildupGain_psi; b.gn++; } });
    return bins.filter(b=>b.n).map(b=>({ bucket:`${b.lo.toFixed(0)}, ${b.hi.toFixed(0)}`, avg_rate:Math.round(b.sum/b.n*1000)/1000, avg_gain:b.gn?Math.round(b.gsum/b.gn*100)/100:null, cycles:b.n, cycle_ids:b.ids.filter(v=>v!=null).sort((x,y)=>x-y) }));
  })();
  const cyc = mode==="cycle";
  const avgDur  = meanOf(pts,"TimeOffTotal");
  const avgRate = meanOf(pts,"BuildupRate_psi_per_min");
  const isRate  = bmode==="rate";
  const bKey    = isRate ? "BuildupRate_psi_per_min" : "BuildupGain_psi";
  const bAvg    = meanOf(pts,bKey);
  const r       = pearson(pts.map(p=>p.TimeOffTotal), pts.map(p=>p.BuildupRate_psi_per_min));
  const nNeg    = pts.filter(p=>p.BuildupRate_psi_per_min<=0).length;
  const fastest = buckets.length ? buckets.reduce((a,b)=>b.avg_rate>a.avg_rate?b:a) : null;
  const busiest = buckets.length ? buckets.reduce((a,b)=>b.cycles>a.cycles?b:a) : null;
  const lineColor = cyc&&!isRate ? C.amber : C.pink;
  /* legend: now sits on top of the plot it describes (inside the plot's card) */
  const legendRow = (
    <div style={{ display:"flex", flexWrap:"wrap", gap:18, marginBottom:8 }}>
      {cyc ? <LegendItem color={C.cyan} text="Shut-in duration (min, bars · left axis)"/> : <LegendItem color={C.purple} text="Cycles in bucket (bars · left axis)"/>}
      <LegendItem color={lineColor} text={cyc?(isRate?"Buildup rate (psi/min, line · right axis)":"Total casing buildup (psi, line · right axis)"):"Average buildup rate (psi/min, line · right axis)"}/>
      <LegendItem color={C.amber} dashed text={cyc?"Window averages (dashed)":"Window average buildup rate (dashed)"}/>
      {cyc && <span style={{ display:"inline-flex", alignItems:"center", gap:6, fontFamily:MONO, fontSize:10, color:C.muted }}><span style={{ width:8, height:8, borderRadius:"50%", background:C.red }}/>No net casing gain</span>}
    </div>
  );
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14, animation:"fade-up 0.5s ease 0.35s both" }}>
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderTop:`3px solid ${C.cyan}`, borderRadius:14, padding:"18px 18px 16px" }}>
        <div style={{ fontFamily:SANS, fontWeight:700, fontSize:13, color:C.text }}>Buildup Rate vs Shut-In Duration</div>
        <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, marginTop:2, marginBottom:12 }}>
          {cyc ? "One plot, cycle by cycle: bars show how long each cycle was shut in, the line shows how fast the casing built up during it."
               : "One plot, cycles grouped by shut-in duration: bars show how many cycles fall in each range, the line shows their average buildup rate."}
        </div>
        {pts.length>0 && (<>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(170px, 1fr))", gap:12, marginBottom:12 }}>
            <MiniStat label="Average shut-in duration" value={`${f1(avgDur)} min`} sub={`${pts.length} cycles in window`} color={C.cyan}/>
            <MiniStat label="Average buildup rate" value={`${f3(avgRate)} psi/min`} sub={`${nNeg} cycle${nNeg===1?"":"s"} with no net casing gain`} color={C.pink}/>
            <MiniStat label="Duration ↔ rate correlation" value={r==null?"n/a":`r = ${r.toFixed(2)}`} sub="−1 = longer shut-in, slower buildup · +1 = faster" color={r==null?C.muted:Math.abs(r)<0.2?C.muted:r>0?C.green:C.orange}/>
          </div>
          {!cyc && fastest && busiest && <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, lineHeight:1.7, marginBottom:10 }}>
            <> Fastest average buildup is in the <span style={{ color:C.pink }}>{fastest.bucket} min</span> bucket ({f3(fastest.avg_rate)} psi/min); most cycles ({busiest.cycles}) sit in the <span style={{ color:C.purple }}>{busiest.bucket} min</span> bucket.</>
          </div>}
        </>)}
      </div>

      <ToggleBtns color={C.cyan} value={mode} onChange={setMode}
        options={[{ id:"cycle", label:"PER CYCLE" },{ id:"bucket", label:"BY SHUT-IN DURATION" }]}/>

      {pts.length===0
        ? <div style={{ fontFamily:MONO, fontSize:11, color:C.muted, padding:20 }}>No cycles with both shut-in time and buildup rate in this window.</div>
        : cyc ? (
          <ChartCard title={isRate?"Shut-In Duration & Pressure Buildup Rate per Cycle":"Shut-In Duration & Total Casing Buildup per Cycle"}
            subtitle={isRate?"bars = minutes shut in · line = casing psi gained per minute of shut-in (at open − at close) · dashed lines = window averages":"bars = minutes shut in · line = casing psi gained over each shut-in (at open − at close) · dashed lines = window averages"}
            summaryTitle={isRate?"Shut-In Duration & Buildup Rate per Cycle":"Shut-In Duration & Total Casing Buildup per Cycle"}
            summarySubtitle={isRate?"bars = minutes shut in, line = buildup rate in psi per minute of shut-in, dashed lines = window averages":"bars = minutes shut in, line = total psi gained over each shut-in, dashed lines = window averages"}
            accentColor={lineColor} height={370} delay={0}>
            <div style={{ height:"100%", display:"flex", flexDirection:"column" }}>
              <ToggleBtns color={lineColor} value={bmode} onChange={setBmode}
                options={[{ id:"rate", label:"RATE (psi/min)" },{ id:"gain", label:"TOTAL BUILDUP (psi)" }]}/>
              {legendRow}
              <div style={{ flex:1, minHeight:0 }}>
                {DurationRateChart({ data:pts, mode:"cycle", hasT,
                  metricKey:bKey, metricName:isRate?"Buildup Rate":"Casing Buildup", metricUnit:isRate?"psi/min":"psi",
                  barKey:"TimeOffTotal", barName:"Shut-In Duration", barColor:C.cyan, lineColor,
                  barAvg:avgDur, barAvgLabel:`avg ${f1(avgDur)} min`, lineAvg:bAvg, lineAvgLabel:`avg ${isRate?f3(bAvg):f1(bAvg)}`, windowAvgRate:avgRate })}
              </div>
            </div>
          </ChartCard>
        ) : (
          <ChartCard title="Cycles & Average Buildup Rate per Shut-In Duration Bucket"
            subtitle="bars = how many cycles fall in each shut-in length range · line = their average buildup rate (psi/min) · dashed line = window average"
            accentColor={C.purple} height={370} delay={0}>
            <div style={{ height:"100%", display:"flex", flexDirection:"column" }}>
              {legendRow}
              <div style={{ flex:1, minHeight:0 }}>
                {DurationRateChart({ data:buckets, mode:"bucket", hasT,
                  metricKey:"avg_rate", metricName:"Avg Buildup Rate", metricUnit:"psi/min",
                  barKey:"cycles", barName:"Cycles in bucket", barColor:C.purple, lineColor:C.pink,
                  lineAvg:avgRate, lineAvgLabel:`avg ${f3(avgRate)} psi/min`, windowAvgRate:avgRate })}
              </div>
            </div>
          </ChartCard>
        )}
    </div>
  );
}

/* ───────────── SHUT-IN REFERENCE: FLOW TIMING / EFFICIENCY INTERPRETATION / CYCLE TABLE (tabbed, like the GLR reference card) ───────────── */
const EFF_CLASSES = [
  { id:"over",  label:"OVER-SHUT-IN",  range:"< 0.8×",     color:"blue",   test:e=>e<0.8,
    assess:"Actual shut-in exceeds the required minimum by more than 25%. Plunger fall and gas / liquid travel were fully satisfied; the additional time is only productive while the casing pressure is still rising.",
    action:"Confirm against the Buildup Plateau panel. Where the plateau is confirmed and lift pressure is proven, open earlier." },
  { id:"opt",   label:"OPTIMAL",       range:"0.8, 1.2×", color:"green",  test:e=>e>=0.8&&e<=1.2,
    assess:"Actual shut-in is within about ±20% of the required minimum: the plunger had time to reach bottom and the casing had adequate time to recharge.",
    action:"Maintain current timer settings and keep monitoring." },
  { id:"under", label:"UNDER-SHUT-IN", range:"> 1.2×",     color:"orange", test:e=>e>1.2,
    assess:"Actual shut-in is more than ~17% shorter than the required minimum. The plunger may not have reached bottom and the casing may not have fully recharged, which risks weak lift energy and liquid loading.",
    action:"Extend shut-in toward the required minimum, then re-check arrival velocity and buildup." },
];
const effClassOf = e => (fin(e) ? EFF_CLASSES.find(c=>c.test(e)) : null);
const meanOf = (rows,k) => { const v = vals(rows,k); return v.length ? v.reduce((a,b)=>a+b,0)/v.length : null; };

const TH = () => ({ padding:"8px 12px", textAlign:"left", color:C.muted, fontSize:9, letterSpacing:1, borderBottom:`1px solid ${C.border}`, background:C.panel, whiteSpace:"nowrap", position:"sticky", top:0 });
const TD = () => ({ padding:"7px 12px", borderBottom:`1px solid ${C.border}`, fontFamily:MONO, fontSize:11, whiteSpace:"nowrap" });

/* ───────────── PER-CYCLE ASSESSMENT (flow-timing tab): status + diagnosis + recommended action, computed from the cycle's own numbers ───────────── */
const LVL = { critical:{ label:"CRITICAL", color:"red" }, warning:{ label:"WARNING", color:"amber" }, good:{ label:"GOOD", color:"green" }, info:{ label:"NO RATING", color:"muted" } };
const mk = (lvl, headline, lines=[], note=null) => ({ lvl, label:LVL[lvl].label, color:LVL[lvl].color, headline, lines, note });

function assessTiming(d) {
  const act=d.TimeOffTotal, req=d.MinShutInTime_min, eff=d.ShutInEfficiency, gain=d.BuildupGain_psi, rate=d.BuildupRate_psi_per_min, af=d.TimeAfterFlow, vel=d.ArrivalVelocity;
  if (!fin(act) || !fin(req) || !fin(eff)) return mk("info","Not enough data to rate this cycle");
  const delta = act-req, abs = Math.abs(delta);
  const velOk = fin(vel) && vel>=350 && vel<=1200, velLow = fin(vel) && vel<350;
  const noGain = fin(gain) && gain<=0, hasGain = fin(gain) && gain>0;
  const note = fin(af) && af>720 ? `Check data: afterflow of ${Math.round(af).toLocaleString()} min (~${(af/60).toFixed(0)} h) is unusually long - well may have stayed on or the record has a gap.` : null;
  const gainLine = fin(gain) ? (gain<=0 ? "Casing pressure did not rise over this shut-in (no net recharge)." : `Casing gained ${f1(gain)} psi (${f3(rate)} psi/min) in this time.`) : null;
  const arrLine = fin(vel) ? (velOk ? `Plunger still arrived at ${f0(vel)} ft/min.` : velLow ? `Plunger arrival velocity was only ${f0(vel)} ft/min (below the 350 ft/min target).` : `Plunger arrival velocity ${f0(vel)} ft/min is above the 1200 ft/min limit.`) : null;
  if (eff>1.5) {
    if (velOk && hasGain) return mk("warning",`Under-shut-in by ${f1(abs)} min (${(act/req*100).toFixed(0)}% of required) - but the plunger still lifted`,
      [arrLine, gainLine].filter(Boolean), note);
    return mk("critical",`Severely under-shut-in: ${f1(abs)} min short (${(act/req*100).toFixed(0)}% of required)`,
      [gainLine, arrLine].filter(Boolean), note);
  }
  if (eff>1.2) {
    if (velLow || noGain) return mk("critical",`Under-shut-in by ${f1(abs)} min with weak ${velLow?"plunger arrival":"casing recharge"}`,
      [arrLine, gainLine].filter(Boolean), note);
    return mk("warning",`Slightly under-shut-in: ${f1(abs)} min short`, [arrLine, gainLine].filter(Boolean), note);
  }
  if (eff>=0.8) {
    if (velLow) return mk("warning",`Shut-in matches the required minimum, but plunger arrival is slow (${f0(vel)} ft/min)`, [gainLine].filter(Boolean), note);
    return mk("good","Shut-in matches the required minimum", [arrLine, gainLine].filter(Boolean), note);
  }
  if (eff>=0.5) return mk("warning",`Over-shut-in: ${f1(abs)} min longer than required (${(act/req).toFixed(1)}×)`,
    [noGain ? "The casing gained no pressure, so the extra time added nothing." : `Casing gained ${f1(gain)} psi over the longer shut-in.`], note);
  return mk("critical",`Excessive shut-in: ${f1(abs)} min beyond required (${(act/req).toFixed(1)}×)`,
    [noGain ? "No casing pressure gain over a very long shut-in - production time is being lost." : `Shut-in is more than double what is required (casing gain ${f1(gain)} psi).`], note);
}
function assessPressure(d, medGain) {
  const gain=d.BuildupGain_psi, eff=d.ShutInEfficiency;
  if (!fin(gain)) return mk("info","Casing pressures missing for this cycle");
  const under = fin(eff) && eff>1.2;
  if (gain<=0) return under
    ? mk("critical",`No casing recharge (${f1(gain)} psi) on a short shut-in`,["Shut-in was also shorter than required, so lift energy is unlikely to be adequate. Extend shut-in."])
    : mk("warning",`No net casing recharge (${f1(gain)} psi)`,["Casing at open is not above casing at close. Check whether the well was already at equilibrium or the pressure data is reliable."]);
  if (medGain && gain<0.5*medGain) return mk("warning",`Weak recharge: ${f1(gain)} psi (${(gain/medGain*100).toFixed(0)}% of the window's typical ${f1(medGain)} psi)`,
    [under ? "Short shut-in is limiting the pressure built - extend shut-in." : "Recharge is low for the time allowed - check for liquid loading or a restriction."]);
  return mk("good",`Healthy recharge: ${f1(gain)} psi`,[medGain?`In line with the window's typical gain of ${f1(medGain)} psi.`:"Casing pressure rose over the shut-in."]);
}

function StatusDot({ cx, cy, payload }) {
  if (cx==null || cy==null || !payload?._a) return null;
  return <circle cx={cx} cy={cy} r={3.5} fill={C[payload._a.color]} stroke={C.card} strokeWidth={1}/>;
}

function assessAfterflow(d, med) {
  const af = d.TimeAfterFlow;
  if (!fin(af)) return mk("info","Afterflow missing for this cycle");
  const x = med ? af/med : null;
  if (af>10080) return mk("critical",`Afterflow of ${Math.round(af).toLocaleString()} min (~${(af/1440).toFixed(1)} days)`, [], "Longer than 7 days - the well stayed open or the cycle record has a gap.");
  if (af>720)   return mk("warning",`Afterflow of ${Math.round(af).toLocaleString()} min (~${(af/60).toFixed(0)} h)${x?` - ${x.toFixed(0)}× the typical ${f0(med)} min`:""}`, [], "Unusually long - check for liquid fallback or a missing close event.");
  return mk("good",`Afterflow of ${f0(af)} min is within the normal range${med?` (typical ${f0(med)} min)`:""}`);
}

/* Hover card for the flow-timing plots: values + local status badge (ground truth) + AI recommendation (same /ai-advice engine as the other pages) */
function AssessTip({ active, payload, mode }) {
  const { advice, loading, fetchAdvice, clearAdvice } = useAIAdvice();
  const [pos, setPos]     = useState({ x:0, y:0 });
  const [asked, setAsked] = useState(null);
  const d = active && payload?.length ? (payload[0].payload || {}) : {};
  const a = d._a;
  const primary = mode==="pressure" ? "CsgAtOpen" : mode==="afterflow" ? "TimeAfterFlow" : "TimeOffTotal";
  const key = active && a && a.lvl!=="info" && fin(d[primary]) ? `${mode}_${d.index}_${d[primary]}_${d.BuildupGain_psi}` : null;

  useEffect(()=>{
    if(!active) return;
    const onMove = e => { const TW=350, TH=440; const x=e.clientX+18, y=e.clientY-10;
      setPos({ x:x+TW>window.innerWidth?e.clientX-TW-18:x, y:y+TH>window.innerHeight?window.innerHeight-TH-16:y }); };
    window.addEventListener("mousemove",onMove);
    return ()=>window.removeEventListener("mousemove",onMove);
  },[active]);

  useEffect(()=>{
    if(!key) return;
    const t = setTimeout(()=>{
      const r2 = v => Math.round(v*100)/100;
      const st = `${a.label} - ${a.headline}`;
      const f = (field,label,value,unit) => fin(value) ? { field, label, value:r2(value), unit, ...(field===primary ? { status:st } : {}) } : null;
      const fields = (mode==="pressure" ? [
        f("CsgAtOpen","Casing pressure at open",d.CsgAtOpen,"psi"), f("CsgAtClose","Casing pressure at close",d.CsgAtClose,"psi"),
        f("BuildupGain_psi","Casing gain over the shut-in (open minus close)",d.BuildupGain_psi,"psi"),
        f("TimeOffTotal","Actual shut-in",d.TimeOffTotal,"min"), f("MinShutInTime_min","Required minimum shut-in",d.MinShutInTime_min,"min"),
        f("ShutInEfficiency","Shut-in efficiency (required / actual)",d.ShutInEfficiency,"x"),
      ] : [
        f("TimeOffTotal","Actual shut-in",d.TimeOffTotal,"min"), f("MinShutInTime_min","Required minimum shut-in",d.MinShutInTime_min,"min"),
        f("ShutInEfficiency","Shut-in efficiency (required / actual)",d.ShutInEfficiency,"x"),
        f("ArrivalMinute","Plunger arrival",d.ArrivalMinute,"min after open"), f("ArrivalVelocity","Plunger arrival velocity",d.ArrivalVelocity,"ft/min"),
        f("TimeAfterFlow","Afterflow",d.TimeAfterFlow,"min"),
        f("BuildupGain_psi","Casing gain over the shut-in (open minus close)",d.BuildupGain_psi,"psi"), f("BuildupRate_psi_per_min","Buildup rate",d.BuildupRate_psi_per_min,"psi/min"),
      ]).filter(Boolean);
      const ctx = Object.fromEntries(fields.map(x=>[x.field,x.value]));
      setAsked(key);
      fetchAdvice(primary, d[primary], mode==="pressure"?"psi":"min", d.index, ctx, fields);
    }, 350);
    return ()=>clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[key]);
  useEffect(()=>{ if(!active) clearAdvice(); },[active, clearAdvice]);

  if(!active || !payload?.length) return null;
  const ac = a ? C[a.color] : C.muted;
  const aiC = !advice ? C.muted : advice.startsWith("■")||advice.startsWith("▽") ? C.red : advice.startsWith("▲")||advice.startsWith("▶") ? C.amber : C.green;
  const aiText = advice ? advice.replace(/^[◆▲▶■▽]\s*/, "") : "";
  const rowsT = mode==="pressure"
    ? [["Casing at close", d.CsgAtClose, "psi", C.blue],["Casing at open", d.CsgAtOpen, "psi", C.teal],["Casing gain", d.BuildupGain_psi, "psi", C.amber],["Shut-in", d.TimeOffTotal, "min", C.cyan]]
    : mode==="afterflow"
    ? [["Afterflow", d.TimeAfterFlow, "min", C.green],["Actual shut-in", d.TimeOffTotal, "min", C.cyan],["Plunger arrival", d.ArrivalMinute, "min", C.amber],["Arrival velocity", d.ArrivalVelocity, "ft/min", C.text]]
    : [["Actual shut-in", d.TimeOffTotal, "min", C.cyan],["Required minimum", d.MinShutInTime_min, "min", C.purple],["Efficiency", d.ShutInEfficiency, "×", C.text],["Plunger arrival", d.ArrivalMinute, "min", C.amber],["Arrival velocity", d.ArrivalVelocity, "ft/min", C.text],["Casing gain", d.BuildupGain_psi, "psi", C.amber]];
  return createPortal(
    <div style={{ position:"fixed", left:pos.x, top:pos.y, zIndex:999999, pointerEvents:"none", width:340, background:C.card, border:`1px solid ${ac}55`, borderLeft:`4px solid ${ac}`, borderRadius:12, padding:"12px 14px", fontFamily:MONO, fontSize:10, lineHeight:1.6, boxShadow:"0 12px 40px rgba(0,0,0,0.8)" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:8, marginBottom:6 }}>
        <span style={{ color:C.cyan, fontWeight:700, fontSize:12 }}>Cycle {d.index}</span>
        {a && <span style={{ color:ac, background:`${ac}1a`, border:`1px solid ${ac}44`, borderRadius:5, padding:"1px 8px", fontWeight:700, letterSpacing:1 }}>{a.label}</span>}
      </div>
      {d.time && <div style={{ color:C.muted, fontSize:9, marginBottom:6 }}>{d.time}</div>}
      <div style={{ borderTop:`1px solid ${C.border}`, borderBottom:`1px solid ${C.border}`, padding:"5px 0", marginBottom:8 }}>
        {rowsT.filter(r=>fin(r[1])).map(([k,v,u,c])=>(
          <div key={k} style={{ display:"flex", justifyContent:"space-between", gap:10 }}>
            <span style={{ color:C.muted }}>{k}</span>
            <span style={{ color:c, fontWeight:700 }}>{u==="×"?v.toFixed(2):v.toFixed(1)} <span style={{ color:C.muted, fontWeight:400 }}>{u}</span></span>
          </div>
        ))}
      </div>
      {a && <div style={{ color:ac, fontWeight:700, fontSize:11, marginBottom:6 }}>{a.headline}</div>}
      {a?.note && <div style={{ color:C.orange, marginBottom:6 }}>{a.note}</div>}
      {key && (
        <div style={{ borderLeft:`3px solid ${aiC}`, paddingLeft:8 }}>
          <div style={{ color:C.muted, fontSize:9, letterSpacing:1, marginBottom:2 }}>AI RECOMMENDATION</div>
          {(asked!==key || loading) ? <span style={{ color:C.muted }}>Getting AI recommendation...</span>
            : advice ? <span style={{ color:aiC }}>{aiText}</span>
            : <span style={{ color:C.muted }}>Hover a point for AI advice</span>}
        </div>
      )}
    </div>, document.body);
}

function ShutInReferencePanel({ rows, oppRows, TTip }) {
  const [tab,   setTab]   = useState("timing");
  const [tview, setTview] = useState("timing");
  const [tbl,   setTbl]   = useState("25");
  const [hid,   setHid]   = useState({});      // lines switched off with the chips above the chart
  const [yfull, setYfull] = useState(false);   // timing / pressure lines: focused y-range vs full range
  const [tmode, setTmode] = useState("cycle");   // cycle table  |  shut-in class table
  const [cfilt, setCfilt] = useState("all");     // all | over | opt | under

  const oppBy = useMemo(()=>new Map((oppRows||[]).map(r=>[r.index,r])),[oppRows]);
  const data  = useMemo(()=>rows.map(d=>({
    ...d,
    BuildupGain_psi: fin(d.CsgAtOpen)&&fin(d.CsgAtClose) ? Math.round((d.CsgAtOpen-d.CsgAtClose)*100)/100 : null,
    // never-negative versions for the reference tables (backend sends them; rebuilt here if an older backend does not)
    BuildupGain_net_psi: fin(d.BuildupGain_net_psi) ? d.BuildupGain_net_psi : (fin(d.CsgAtOpen)&&fin(d.CsgAtClose) ? Math.max(0, Math.round((d.CsgAtOpen-d.CsgAtClose)*100)/100) : null),
    BuildupRate_net_psi_per_min: fin(d.BuildupRate_net_psi_per_min) ? d.BuildupRate_net_psi_per_min : (fin(d.BuildupRate_psi_per_min) ? Math.max(0, d.BuildupRate_psi_per_min) : null),
    // required minimum: use the backend column when sent, otherwise rebuild it from its own definition (efficiency = required minimum / actual shut-in)
    MinShutInTime_min: fin(d.MinShutInTime_min) ? d.MinShutInTime_min
      : (fin(d.ShutInEfficiency)&&fin(d.TimeOffTotal) ? Math.round(d.ShutInEfficiency*d.TimeOffTotal*10)/10 : null),
    cls: effClassOf(d.ShutInEfficiency),
  })).map(d=>({ ...d, delta_min: fin(d.TimeOffTotal)&&fin(d.MinShutInTime_min) ? d.TimeOffTotal-d.MinShutInTime_min : null })),[rows]);

  const hasT = hasStamps(rows);
  const cm   = hasT ? { ...CHART_MARGIN_DEFAULT, bottom:22 } : CHART_MARGIN_DEFAULT;
  const has  = k => data.some(d=>fin(d[k]));
  const axisTick = { fill:C.muted, fontSize:9, fontFamily:MONO };
  const ylab = v => ({ value:v, angle:-90, position:"insideLeft", offset:14, fill:C.muted, fontSize:9, fontFamily:MONO });

  const ALL_TABS = [
    { id:"timing", label:"FLOW TIMING REFERENCE" },
    { id:"table",  label:"CYCLE & CLASS DETAIL" },
  ];
  const VIEWS = {
    timing:   { label:"FLOW TIMING (min)", y:"Minutes", focus:["TimeOffTotal","MinShutInTime_min","ArrivalMinute"], series:[
      { k:"TimeOffTotal",      name:"Actual shut-in (min)",           color:"cyan"   },
      { k:"MinShutInTime_min", name:"Required minimum shut-in (min)", color:"purple" },
      { k:"ArrivalMinute",     name:"Plunger arrival (min)",          color:"amber"  } ] },
    pressure: { label:"CASING AT CLOSE vs OPEN (psi)", y:"psi", series:[
      { k:"CsgAtClose", name:"Casing at close (psi)", color:"blue" },
      { k:"CsgAtOpen",  name:"Casing at open (psi)",  color:"teal" } ] },
  };
  // only offer views / tabs that actually have something to compare
  const availViews = Object.entries(VIEWS).filter(([,v])=>v.series.filter(s=>has(s.k)).length>=2);
  const curView    = availViews.some(([id])=>id===tview) ? tview : (availViews[0]?.[0] || null);
  const view   = curView ? VIEWS[curView] : null;
  const series = view ? view.series.filter(s=>has(s.k)) : [];
  const cdata  = data.filter(d=>series.some(s=>fin(d[s.k])));
  const shown = series.filter(sr=>!hid[sr.k]);
  const medGain = medianOf(vals(data,"BuildupGain_psi").filter(v=>v>0));
  const statusKey = curView==="pressure" ? "CsgAtOpen" : "TimeOffTotal";
  const cdataA = cdata.map(d=>({ ...d, _a: curView==="pressure" ? assessPressure(d, medGain) : assessTiming(d) }));
  const cnt = l => cdataA.filter(d=>d._a.lvl===l).length;
  const afRaw  = cdataA.filter(d=>fin(d.TimeAfterFlow));
  const afMed  = medianOf(afRaw.map(d=>d.TimeAfterFlow));
  const afData = afRaw.map(d=>({ ...d, _a: assessAfterflow(d, afMed) }));
  // Focused y-range: a few extreme afterflow readings (thousands of min) flatten every other line, so the axis is capped
  // just above the normal spread of the core series (95th percentile) and the cut-off values are reported, not hidden.
  // eslint-disable-next-line no-unused-vars
  let cap = null, nClip = 0, clipMax = null;
  if (view && view.focus && !yfull && shown.length) {
    const core = cdata.flatMap(d=>shown.map(sr=>d[sr.k])).filter(fin).sort((a,b)=>a-b);
    if (core.length) {
      const p95 = core[Math.floor(0.95*(core.length-1))];
      const x = Math.max(p95*1.25, 1), mag = Math.pow(10, Math.floor(Math.log10(x))), step = mag/2;
      const c = Math.ceil(x/step)*step;
      const over = shown.flatMap(sr=>cdata.map(d=>d[sr.k])).filter(v=>fin(v)&&v>c);
      if (over.length) { cap = c; nClip = over.length; clipMax = Math.max(...over); }
    }
  }
  const canToggleRange = !!(view && view.focus && (cap!=null || yfull));
  // plotted values are clamped to the axis cap (tooltip still reads the real value) so no line shoots off the chart
  const cdataP = cdataA.map(d=>{ const o = { ...d }; series.forEach(sr=>{ o[`p_${sr.k}`] = fin(d[sr.k]) ? (cap!=null ? Math.min(d[sr.k], cap) : d[sr.k]) : null; }); return o; });
  const TABS = ALL_TABS.filter(t => t.id==="timing" ? availViews.length>0 : data.length>0);
  const tabNow = TABS.some(t=>t.id===tab) ? tab : (TABS[0]?.id || null);
  const missingFlow = !has("ArrivalMinute") || !has("TimeAfterFlow");

  const nCl = data.filter(d=>d.cls).length;
  const classRows = EFF_CLASSES.map(c=>{
    const g = data.filter(d=>d.cls && d.cls.id===c.id);
    // when a band is empty, find the cycle that came closest to it so the row still says something useful
    let nearest = null;
    if (!g.length) {
      const lo = c.id==="over" ? -Infinity : c.id==="opt" ? 0.8 : 1.2, hi = c.id==="over" ? 0.8 : c.id==="opt" ? 1.2 : Infinity;
      data.filter(d=>fin(d.ShutInEfficiency)).forEach(d=>{
        const dist = d.ShutInEfficiency<lo ? lo-d.ShutInEfficiency : d.ShutInEfficiency>hi ? d.ShutInEfficiency-hi : 0;
        if (!nearest || dist<nearest.dist) nearest = { dist, d };
      });
    }
    return { ...c, n:g.length, pct:nCl?g.length/nCl*100:0, nearest:nearest?nearest.d:null,
      shut:meanOf(g,"TimeOffTotal"), minShut:meanOf(g,"MinShutInTime_min"), gap:meanOf(g,"delta_min"), rate:meanOf(g,"BuildupRate_net_psi_per_min"), gain:meanOf(g,"BuildupGain_net_psi") };
  });

  // ---- computed findings for the Diagnosis tab ----
  const dom = [...classRows].sort((a,b)=>b.n-a.n)[0];
  const underG = data.filter(d=>d.cls&&d.cls.id==="under");
  const medShort = medianOf(underG.map(d=>d.MinShutInTime_min-d.TimeOffTotal));
  const withGain = data.filter(d=>fin(d.BuildupGain_psi));
  const noGain   = withGain.filter(d=>d.BuildupGain_psi<=0);
  const optRow   = classRows.find(c=>c.id==="opt");
  const findings = [];
  if (dom && dom.n) {
    findings.push({ color:C[dom.color], title:`Where the cycles sit: ${dom.pct.toFixed(1)}% ${dom.label}`,
      text:`${dom.n} of ${nCl} cycles fall in the ${dom.label} band (${dom.range}). Average shut-in in this band is ${f1(dom.shut)} min against ${f1(dom.minShut)} min required.` });
  }
  if (underG.length && medShort!=null) {
    findings.push({ color:C.orange, title:`Typical shortfall: ${f1(medShort)} min`,
      text:`The median under-shut-in cycle closes ${f1(medShort)} min before the required minimum is reached. Closing the gap would take the average shut-in from ${f1(meanOf(underG,"TimeOffTotal"))} min to about ${f1(meanOf(underG,"MinShutInTime_min"))} min.` });
  }
  if (withGain.length) {
    const share = noGain.length/withGain.length*100;
    findings.push({ color:share>=50?C.red:share>=20?C.amber:C.green, title:`Casing recharge: ${noGain.length} of ${withGain.length} cycles with no net gain`,
      text: share>=20
        ? `In ${share.toFixed(0)}% of cycles the casing pressure at open was not higher than at close, so no net recharge was recorded over the shut-in. Overall average gain is ${f1(meanOf(withGain,"BuildupGain_psi"))} psi per shut-in.`
        : `Most cycles recorded a positive casing gain (average ${f1(meanOf(withGain,"BuildupGain_psi"))} psi per shut-in), so shut-in time is converting into usable pressure.` });
  }
  if (optRow) {
    findings.push({ color:optRow.n?C.green:C.muted, title:optRow.n?`${optRow.n} cycle${optRow.n===1?"":"s"} within the optimal band`:"No cycle reached the optimal band",
      text: optRow.n
        ? `Optimal cycles average ${f1(optRow.shut)} min shut-in (required ${f1(optRow.minShut)} min) with ${f3(optRow.rate)} psi/min buildup.`
        : (optRow.nearest ? `The closest was cycle C${optRow.nearest.index} at ${f2(optRow.nearest.ShutInEfficiency)}× (shut-in ${f1(optRow.nearest.TimeOffTotal)} min vs ${f1(optRow.nearest.MinShutInTime_min)} min required).` : "Efficiency could not be evaluated in this window.") });
  }
  const headline = dom && dom.n
    ? (dom.id==="under" ? "Shut-in is mostly shorter than the flow-timing requirement - extending it is the main lever."
      : dom.id==="over" ? "Shut-in mostly exceeds the required minimum - the extra time needs to be justified by continued buildup."
      : "Shut-in is mostly in line with the flow-timing requirement.")
    : "";

  const sorted = [...data].sort((a,b)=>((b._t??-1)-(a._t??-1))||(b.index-a.index));
  const fRows  = cfilt==="all" ? sorted : sorted.filter(d=>d.cls && d.cls.id===cfilt);
  const tRows  = tbl==="all" ? fRows : fRows.slice(0, Number(tbl));
  const medRate = (() => { const v = vals(data,"BuildupRate_psi_per_min").sort((a,b)=>a-b); return v.length ? v[Math.floor(v.length/2)] : null; })();

  return (
    <div style={{ background:C.card, border:`1px solid ${C.border}`, borderTop:`3px solid ${C.cyan}`, borderRadius:14, padding:"20px 18px", animation:"fade-up 0.5s ease 0.4s both" }}>
      <div style={{ marginBottom:14 }}>
        <div style={{ fontFamily:SANS, fontWeight:700, fontSize:13, color:C.text }}>Shut-In Reference: Flow Timing, Buildup &amp; Efficiency</div>
        <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, marginTop:2 }}>
          Flow timing (plunger arrival, afterflow, required minimum shut-in) is the reference for reading pressure buildup, shut-in and shut-in efficiency · follows the DATA WINDOW
        </div>
      </div>
      {TABS.length>1 && <ToggleBtns color={C.cyan} value={tabNow} onChange={setTab} options={TABS}/>}

      {data.length===0 && <div style={{ fontFamily:MONO, fontSize:11, color:C.muted, padding:20 }}>No cycles in the selected window.</div>}

      {data.length>0 && tabNow==="timing" && (<>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(170px, 1fr))", gap:12, marginBottom:14 }}>
          {[
            { k:"TimeOffTotal",      label:"Avg shut-in",         sub:"actual",                      color:C.cyan   },
            { k:"MinShutInTime_min", label:"Avg required shut-in", sub:"plunger fall + gas / liquid travel", color:C.purple },
            { k:"ArrivalMinute",     label:"Avg plunger arrival", sub:"minutes after open",          color:C.amber  },
            { k:"TimeAfterFlow",     label:"Typical (median) afterflow", sub:`flow after arrival · average ${f0(meanOf(data,"TimeAfterFlow"))} min is inflated by a few very long cycles`, color:C.green  },
          ].filter(t=>has(t.k)).map(t=><MiniStat key={t.k} label={t.label} value={`${f1(t.k==="TimeAfterFlow"?medianOf(vals(data,t.k)):meanOf(data,t.k))} min`} sub={t.sub} color={t.color}/>)}
        </div>
        {availViews.length>1 && <ToggleBtns color={C.cyan} value={curView} onChange={setTview} options={availViews.map(([id,v])=>({ id, label:v.label }))}/>}
        {canToggleRange && (
          <div style={{ display:"flex", flexWrap:"wrap", alignItems:"center", gap:10, marginBottom:8 }}>
            <ToggleBtns color={C.amber} value={yfull?"full":"focus"} onChange={v=>setYfull(v==="full")} options={[{ id:"focus", label:"FOCUSED RANGE" },{ id:"full", label:"FULL RANGE" }]}/>
          </div>
        )}
        {view && (
          <div style={{ display:"flex", flexWrap:"wrap", alignItems:"center", gap:10, marginBottom:10 }}>
            <span style={{ fontFamily:MONO, fontSize:9, letterSpacing:2, color:C.muted }}>CYCLE STATUS</span>
            <Tag color={C.red}>{cnt("critical")} CRITICAL</Tag>
            <Tag color={C.amber}>{cnt("warning")} WARNING</Tag>
            <Tag color={C.green}>{cnt("good")} GOOD</Tag>
            <span style={{ fontFamily:MONO, fontSize:10, color:C.muted }}>
              Dot colour on the {curView==="pressure"?"casing-at-open":"actual shut-in"} line = status of that cycle · hover any point for the diagnosis and the recommended action.
            </span>
          </div>
        )}
        {view && series.length>1 && (
          <div style={{ display:"flex", flexWrap:"wrap", alignItems:"center", gap:8, marginBottom:10 }}>
            <span style={{ fontFamily:MONO, fontSize:9, letterSpacing:2, color:C.muted }}>SHOW LINES</span>
            {series.map(sr=>{
              const on = !hid[sr.k];
              return (
                <button key={sr.k} onClick={()=>{ if(on && shown.length<=1) return; setHid(h=>({ ...h, [sr.k]:on })); }}
                  style={{ fontFamily:MONO, fontSize:10, padding:"4px 10px", borderRadius:6, cursor:"pointer", whiteSpace:"nowrap",
                    background:on?`${C[sr.color]}1c`:"transparent", color:on?C[sr.color]:C.muted2, border:`1px solid ${on?C[sr.color]:C.border2}` }}>
                  {on?"●":"○"} {sr.name}{sr.k==="MinShutInTime_min"?" - dashed":""}
                </button>
              );
            })}
          </div>
        )}
        {view && (
          <ChartCard height={380} accentColor={C.cyan}
            title={curView==="pressure"?"Casing Pressure at Open and at Close per Cycle":"Flow Timing per Cycle: Actual vs Required Shut-In, Plunger Arrival"}
            subtitle={curView==="pressure"?"casing pressure when the valve opens against when it closes, per cycle; the gap is the pressure gained over the shut-in":"actual shut-in time against the required minimum shut-in and the minute the plunger arrives, per cycle; dashed line = required minimum"}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={cdataP} margin={cm}>
                <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
                <XAxis dataKey="index" {...CYCLE_XAXIS_PROPS()} height={hasT?20:undefined} label={hasT?undefined:{ value:"Cycle", position:"insideBottom", offset:-18, fill:C.muted, fontSize:9, fontFamily:MONO }}/>
                {dateXAxis(cdataP,"index",5,"Cycle")}
                <YAxis width={60} tick={axisTick} tickLine={false} axisLine={false} domain={cap!=null?[0,cap]:[0,"auto"]} allowDataOverflow={cap!=null} label={ylab(curView==="pressure"?"Casing pressure (psi)":"Time (min)")}/>
                <Tooltip content={<AssessTip mode={curView}/>} cursor={{ stroke:C.muted, strokeDasharray:"3 3" }} wrapperStyle={{ zIndex:99999, overflow:"visible !important" }}/>
                {shown.map(sr=><Line key={sr.k} type="linear" dataKey={`p_${sr.k}`} name={sr.name} stroke={C[sr.color]}
                  strokeWidth={sr.k===statusKey?2:1.3} strokeOpacity={sr.k===statusKey?1:0.75} strokeDasharray={sr.k==="MinShutInTime_min"?"6 3":undefined}
                  dot={sr.k===statusKey?<StatusDot/>:false} activeDot={{ r:5 }} connectNulls isAnimationActive={false}/>)}
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        )}
        {curView==="timing" && afData.length>0 && (<>
          <div style={{ marginTop:14 }}>
          <ChartCard height={300} accentColor={C.green} title="Afterflow per Cycle (min)"
            subtitle="own scale, so long afterflow cycles never flatten the shut-in lines above · minutes the well keeps flowing after the plunger arrives, per cycle; dashed line is the typical (median) afterflow">
            <div style={{ height:"100%", display:"flex", flexDirection:"column" }}>
              <div style={{ display:"flex", flexWrap:"wrap", gap:18, marginBottom:8 }}>
                <LegendItem color={C.green} text="Afterflow (min)"/>
                {afMed!=null && <LegendItem color={C.amber} dashed text="Typical (median) afterflow (dashed)"/>}
                <span style={{ display:"inline-flex", alignItems:"center", gap:6, fontFamily:MONO, fontSize:10, color:C.muted }}>
                  Dot colour = cycle status:
                  <span style={{ width:8, height:8, borderRadius:"50%", background:C.red }}/>critical
                  <span style={{ width:8, height:8, borderRadius:"50%", background:C.amber }}/>warning
                  <span style={{ width:8, height:8, borderRadius:"50%", background:C.green }}/>good
                </span>
              </div>
              <div style={{ flex:1, minHeight:0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={afData} margin={cm}>
                <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
                <XAxis dataKey="index" {...CYCLE_XAXIS_PROPS()} height={hasT?20:undefined} label={hasT?undefined:{ value:"Cycle", position:"insideBottom", offset:-18, fill:C.muted, fontSize:9, fontFamily:MONO }}/>
                {dateXAxis(afData,"index",5,"Cycle")}
                <YAxis width={60} tick={axisTick} tickLine={false} axisLine={false} label={ylab("Afterflow (min)")}/>
                <Tooltip content={<AssessTip mode="afterflow"/>} cursor={{ stroke:C.muted, strokeDasharray:"3 3" }} wrapperStyle={{ zIndex:99999, overflow:"visible !important" }}/>
                {afMed!=null && <ReferenceLine y={afMed} stroke={C.amber} strokeDasharray="5 3" label={{ value:`median ${f0(afMed)} min`, fill:C.amber, fontSize:9, fontFamily:MONO, position:"insideTopRight" }}/>}
                <Line type="linear" dataKey="TimeAfterFlow" name="Afterflow (min)" stroke={C.green} strokeWidth={1.6} dot={<StatusDot/>} activeDot={{ r:4 }} connectNulls isAnimationActive={false}/>
              </LineChart>
            </ResponsiveContainer>
              </div>
            </div>
          </ChartCard>
          </div>
        </>)}
      </>)}

      {data.length>0 && tabNow==="table" && (<>
        <ToggleBtns color={C.cyan} value={tmode} onChange={setTmode} options={[{ id:"cycle", label:`BY CYCLE (${data.length})` },{ id:"class", label:"BY SHUT-IN CLASS" }]}/>

        {tmode==="class" && (<>
          <div style={{ background:`${C.purple}0d`, border:`1px solid ${C.purple}33`, borderLeft:`3px solid ${C.purple}`, borderRadius:10, padding:"12px 16px", marginBottom:14 }}>
            <div style={{ fontFamily:SANS, fontWeight:700, fontSize:13, color:C.text, marginBottom:4 }}>{headline}</div>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(240px, 1fr))", gap:12, marginBottom:16 }}>
            {findings.map(f=>(
              <div key={f.title} style={{ background:`${f.color}0d`, border:`1px solid ${f.color}33`, borderTop:`3px solid ${f.color}`, borderRadius:10, padding:"12px 14px" }}>
                <div style={{ fontFamily:SANS, fontWeight:700, fontSize:12, color:f.color, marginBottom:6 }}>{f.title}</div>
                <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, lineHeight:1.7 }}>{f.text}</div>
              </div>
            ))}
          </div>
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr>{["CLASS","EFFICIENCY BAND","CYCLES","SHARE OF CYCLES","AVG ACTUAL SHUT-IN (min)","AVG REQUIRED SHUT-IN (min)","AVG ACTUAL − REQUIRED (min)","AVG BUILDUP RATE (psi/min)","AVG CASING GAIN (psi)","OPERATIONAL ASSESSMENT","RECOMMENDED ACTION"].map(h=><th key={h} style={TH()}>{h}</th>)}</tr></thead>
              <tbody>
                {classRows.map((c,i)=>(
                  <tr key={c.id} style={{ background:i%2===0?C.card:C.panel }}>
                    <td style={{ ...TD(), fontWeight:700, color:C[c.color] }}>{c.label}</td>
                    <td style={{ ...TD(), color:C.text }}>{c.range}</td>
                    <td style={{ ...TD(), color:C.text, fontWeight:700 }}>{c.n}</td>
                    <td style={{ ...TD(), color:C.muted }}>{f1(c.pct)}%</td>
                    {c.n>0 ? (<>
                      <td style={{ ...TD(), color:C.cyan }}>{f1(c.shut)}</td>
                      <td style={{ ...TD(), color:C.purple }}>{f1(c.minShut)}</td>
                      <td style={{ ...TD(), color:c.gap==null?C.muted:c.gap<0?C.orange:C.blue, fontWeight:700 }}>{c.gap==null?"n/a":Math.abs(c.gap).toFixed(1)}</td>
                      <td style={{ ...TD(), color:C.teal }}>{c.rate==null?"n/a":f3(c.rate)}</td>
                      <td style={{ ...TD(), color:C.amber }}>{c.gain==null?"n/a":f1(c.gain)}</td>
                    </>) : (<>
                      <td style={{ ...TD(), color:C.muted }}>-</td>
                      <td style={{ ...TD(), color:C.muted }}>-</td>
                      <td style={{ ...TD(), color:C.muted }}>-</td>
                      <td style={{ ...TD(), color:C.muted }}>-</td>
                      <td style={{ ...TD(), color:C.muted }}>-</td>
                    </>)}
                    <td style={{ ...TD(), whiteSpace:"normal", minWidth:300, color:C.muted, fontSize:10, lineHeight:1.6 }}>{c.assess}</td>
                    <td style={{ ...TD(), whiteSpace:"normal", minWidth:240, color:C.text, fontSize:10, lineHeight:1.6 }}>{c.action}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>)}

        {tmode==="cycle" && (<>
        <ToggleBtns color={C.cyan} value={cfilt} onChange={setCfilt} options={[{ id:"all", label:"ALL CLASSES" },...classRows.map(c=>({ id:c.id, label:`${c.label} (${c.n})` }))]}/>
        <ToggleBtns color={C.cyan} value={tbl} onChange={setTbl} options={[{ id:"25", label:"LAST 25" },{ id:"50", label:"LAST 50" },{ id:"all", label:`ALL (${data.length})` }]}/>
        <div style={{ overflow:"auto", maxHeight:440 }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead><tr>{["CYCLE","OPENED","SHUT-IN (min)","REQUIRED SHUT-IN (min)","ACTUAL − REQUIRED (min)","EFFICIENCY","CLASS",...(has("ArrivalMinute")?["ARRIVAL (min)"]:[]),...(has("TimeAfterFlow")?["AFTERFLOW (min)"]:[]),"BUILDUP (psi/min)","vs MEDIAN","GAIN (psi)",...(oppRows&&oppRows.length?["PLATEAU VERDICT"]:[])].map(h=><th key={h} style={TH()}>{h}</th>)}</tr></thead>
            <tbody>
              {tRows.map((d,i)=>{
                const v = oppBy.get(d.index)?.verdict; const sv = v ? SI_VERDICT[v] : null;
                const rel = fin(d.BuildupRate_net_psi_per_min) && medRate ? d.BuildupRate_net_psi_per_min/medRate : null;
                return (
                  <tr key={d.index} style={{ background:i%2===0?C.card:C.panel }}>
                    <td style={{ ...TD(), fontWeight:700, color:C.cyan }}>C{d.index}</td>
                    <td style={{ ...TD(), color:C.amber }}>{d._t!=null?fmtDT(d._t):"-"}</td>
                    <td style={{ ...TD(), color:C.text }}>{f1(d.TimeOffTotal)}</td>
                    <td style={{ ...TD(), color:C.purple }}>{f1(d.MinShutInTime_min)}</td>
                    <td style={{ ...TD(), color:d.delta_min==null?C.muted:d.delta_min<0?C.orange:C.blue }}>{d.delta_min==null?"-":Math.abs(d.delta_min).toFixed(1)}</td>
                    <td style={{ ...TD(), color:d.cls?C[d.cls.color]:C.muted, fontWeight:700 }}>{f2(d.ShutInEfficiency)}</td>
                    <td style={TD()}>{d.cls ? <Tag color={C[d.cls.color]}>{d.cls.label}</Tag> : "-"}</td>
                    {has("ArrivalMinute") && <td style={{ ...TD(), color:C.amber }}>{f1(d.ArrivalMinute)}</td>}
                    {has("TimeAfterFlow") && <td style={{ ...TD(), color:C.green }}>{f1(d.TimeAfterFlow)}</td>}
                    <td style={{ ...TD(), color:C.teal }}>{f3(fin(d.BuildupRate_net_psi_per_min)?d.BuildupRate_net_psi_per_min:null)}</td>
                    <td style={{ ...TD(), color:rel==null?C.muted:rel<0.5?C.orange:rel>1.5?C.green:C.text }}>{rel==null?"-":`${rel.toFixed(2)}×`}</td>
                    <td style={{ ...TD(), color:C.text }}>{f1(fin(d.BuildupGain_net_psi)?d.BuildupGain_net_psi:null)}</td>
                    {oppRows&&oppRows.length>0 && <td style={TD()}>{sv ? <Tag color={C[sv.color]}>{sv.label}</Tag> : <span style={{ color:C.muted }}>-</span>}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>)}
      </>)}
    </div>
  );
}

export default function ShutInPage({ TTip }) {
  const { isDark } = useTheme();          
  applyTheme(getColors(isDark));         
  const { apiUrl } = useWell();

  const [summary,    setSummary]    = useState(null);
  const [timeseries, setTimeseries] = useState(null);
  const [effDist,    setEffDist]    = useState(null);
  const [buildup,    setBuildup]    = useState(null);
  const [physics,    setPhysics]    = useState(null);
  const [buildupOpp, setBuildupOpp] = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);

  useEffect(() => {
    async function load() {
      setLoading(true); setError(null);
      try {
        const [s,ts,ed,bu,ph,bo] = await Promise.all([
          axios.get(apiUrl("/shutin/summary")),
          axios.get(apiUrl("/shutin/timeseries?limit=200")),
          axios.get(apiUrl("/shutin/efficiency-distribution")),
          axios.get(apiUrl("/shutin/buildup-analysis")),
          axios.get(apiUrl("/shutin/physics-targets")),
          axios.get(apiUrl("/shutin/buildup-opportunity")).catch(()=>({ data:null })),   // new panel never blocks the rest of the page
        ]);
        setSummary(s.data.summary); setTimeseries(ts.data);
        setEffDist(ed.data); setBuildup(bu.data); setPhysics(ph.data); setBuildupOpp(bo.data);
      } catch(e) { setError(e.message); }
      setLoading(false);
    }
    load();
  }, [apiUrl]);

  /* DATA WINDOW: every cycle in the time-series carries its open time; the window trims what all charts / stats use */
  const tsStamped = useMemo(()=>(timeseries?.data||[]).map(d=>({ ...d, _t:parseT(d.time) })),[timeseries]);
  const tw = useTimeWindow(tsStamped);

  if(loading) return <div style={{ textAlign:"center", padding:80, fontFamily:MONO, fontSize:13, color:C.muted }}>Loading shut-in data...</div>;
  if(error)   return <div style={{ textAlign:"center", padding:80, fontFamily:MONO, fontSize:13, color:C.red }}>Error: {error}</div>;

  const tsData = tw.view;   // "ALL" = every cycle exactly as before
  const vT = tw.view.map(d=>d._t).filter(t=>t!=null);
  const lo = vT.length ? Math.min(...vT) : null, hi = vT.length ? Math.max(...vT) : null;
  const inWin = d => tw.isAll || d._t==null || (lo!=null && d._t>=lo && d._t<=hi);   // other datasets follow the same time span
  const stampRow = d => ({ ...d, _t:parseT(d.time) });
  const physData = (physics?.physics || physics?.per_cycle || []).map(stampRow).filter(inWin);
  const boRows = (buildupOpp?.cycles || []).map(stampRow).filter(inWin);
  const hasT = hasStamps(tsData);
  const CM = hasT ? { ...CHART_MARGIN_DEFAULT, bottom:22 } : CHART_MARGIN_DEFAULT;
  const cycLabel = { value:"Cycle", position:"insideBottom", offset:-18, fill:C.muted, fontSize:9, fontFamily:MONO };

  // stat cards + efficiency split are re-computed on the windowed cycles (ALL = backend numbers)
  const sumX = tw.isAll ? summary : { TimeOffTotal:statsOf(tsData,"TimeOffTotal"), ShutInEfficiency:statsOf(tsData,"ShutInEfficiency"), LiquidHeight_ft:statsOf(tsData,"LiquidHeight_ft") };
  const effV = vals(tsData,"ShutInEfficiency");
  const effDistX = {   // same bands as the diagnosis table: <0.8 over, 0.8-1.2 optimal, >1.2 under
    ...(effDist||{}),
    optimal_count:      effV.filter(e=>e>=0.8&&e<=1.2).length,
    under_shutin_count: effV.filter(e=>e>1.2).length,
    over_shutin_count:  effV.filter(e=>e<0.8).length,
  };

  const effPerCycleData = tsData
    .filter(d => d.ShutInEfficiency != null && isFinite(d.ShutInEfficiency))
    .map((d,i) => {
      const eff = d.ShutInEfficiency;
      let effStatus = undefined;
      if(typeof eff==="number") {
        if(eff>=0.8&&eff<=1.2)       effStatus="GOOD: within optimal range (0.8-1.2×)";
        else if(eff>1.2&&eff<=1.5)   effStatus="WARNING: slightly under-shut-in (1.2-1.5×)";
        else if(eff>1.5)             effStatus="CRITICAL: severely under-shut-in (>1.5×)";
        else if(eff>=0.5&&eff<0.8)   effStatus="WARNING: slightly over-shut-in (0.5-0.8×)";
        else if(eff<0.5)             effStatus="CRITICAL: severely over-shut-in (<0.5×)";
      }
      return {
        index: d.index??i,
        time: d.time, _t: d._t,
        ShutInEfficiency: d.ShutInEfficiency,
        TimeOffTotal: d.TimeOffTotal,
        BuildupRate_psi_per_min: d.BuildupRate_psi_per_min,
        ...(effStatus?{_actualStatus:effStatus}:{}),
      };
    });

  const buildupScatter = (buildup?.timeseries||[]).map(stampRow).filter(inWin).filter(d =>
    d.BuildupRate_psi_per_min!=null && isFinite(d.BuildupRate_psi_per_min) &&
    d.TimeOffTotal!=null && d.TimeOffTotal>0
  );

  const physBreakdown = tw.isAll ? physData.filter(d=>d.gas_travel_min!=null).slice(0,60) : physData.filter(d=>d.gas_travel_min!=null).slice(-60);

  function effColor(val) {
    if(val==null) return C.cyan;
    if(val<0.8)  return C.blue;
    if(val>1.2)  return C.orange;
    return C.green;
  }

  function EffDot(props) {
    const {cx,cy,payload}=props;
    if(!cx||!cy) return null;
    return <circle cx={cx} cy={cy} r={3} fill={effColor(payload?.ShutInEfficiency)} stroke="none" fillOpacity={0.85}/>;
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      {tsStamped.some(d=>d._t!=null) && (
        <DataWindowBar ctl={tw} total={tsStamped.length} isDark={isDark}
          note="The window applies to every Shut-In chart, the stat cards and the efficiency split."/>
      )}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(175px, 1fr))", gap:14 }}>
        <StatCard label="Avg Shut-In Time" value={sumX?.TimeOffTotal?.mean} unit=" min" color={C.cyan}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><rect x="9" y="8" width="2" height="8" rx="1"/><rect x="13" y="8" width="2" height="8" rx="1"/></svg>}
          sub={`Max: ${sumX?.TimeOffTotal?.max?.toFixed(1)??"-"} min`} delay={0}/>
        <StatCard label="Shut-In Efficiency" value={sumX?.ShutInEfficiency?.mean} unit="×" color={sumX?.ShutInEfficiency?.mean>1?C.orange:C.green}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>}
          sub="1.0 = optimal, >1 = under-shut-in" delay={0.05}/>
        <StatCard label="Liquid Height" value={sumX?.LiquidHeight_ft?.mean} unit=" ft" color={C.blue}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></svg>}
          sub="estimated from casing / tubing pressure" delay={0.1}/>
      </div>

      <BuildupOpportunityPanel rows={boRows} basis={buildupOpp?.basis} TTip={TTip}/>

      <div style={{ display:"grid", gridTemplateColumns:"1fr", gap:20 }}>
        <ChartCard title="Shut-In Efficiency per Cycle"
          subtitle="< 0.8 = over-shut-in  |  1.0 = optimal  |  > 1.2 = under-shut-in"
          summarySubtitle="shut-in efficiency = required minimum shut-in divided by actual shut-in; below 0.8 = over-shut-in, 1.0 = optimal, above 1.2 = under-shut-in"
          accentColor={C.purple} height={300} delay={0.15}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={effPerCycleData} margin={CM}>
              <defs>
                <linearGradient id="geff2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={C.purple} stopOpacity={0.25}/>
                  <stop offset="95%" stopColor={C.purple} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
              <XAxis dataKey="index" {...PLAIN_XAXIS_PROPS()} height={hasT?20:undefined} label={hasT?undefined:cycLabel}/>
              {dateXAxis(effPerCycleData,"index",5,"Cycle")}
              <YAxis tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false} tickFormatter={v=>v.toFixed(2)} label={{ value:"Efficiency (×)", angle:-90, position:"insideLeft", offset:14, fill:C.muted, fontSize:9, fontFamily:MONO }}/>
              <Tooltip content={<TTip />} wrapperStyle={{ zIndex:9999, overflow:"visible" }}/>
              <ReferenceLine y={1.0} stroke={C.green} strokeWidth={1.5} strokeDasharray="6 3" label={{ value:"Optimal 1.0", fill:C.green, fontSize:9, fontFamily:MONO, position:"insideTopRight" }}/>
              <ReferenceLine y={0.8} stroke={C.blue}  strokeWidth={1}   strokeDasharray="4 2" label={{ value:"0.8", fill:C.blue, fontSize:9, fontFamily:MONO, position:"insideTopRight" }}/>
              <ReferenceLine y={1.2} stroke={C.orange} strokeWidth={1}  strokeDasharray="4 2" label={{ value:"1.2", fill:C.orange, fontSize:9, fontFamily:MONO, position:"insideTopRight" }}/>
              <Area type="monotone" dataKey="ShutInEfficiency" name="Shut-In Efficiency" stroke={C.purple} fill="url(#geff2)" strokeWidth={2} dot={<EffDot/>} activeDot={{ r:5, stroke:C.purple, strokeWidth:2 }}/>
            </ComposedChart>
          </ResponsiveContainer>
          {effDistX && (
            <div style={{ display:"flex", gap:16, justifyContent:"center", marginTop:8 }}>
              <span style={{ fontFamily:MONO, fontSize:10, color:C.green }}>◆ Optimal: {effDistX.optimal_count}</span>
              <span style={{ fontFamily:MONO, fontSize:10, color:C.orange }}>↑ Under: {effDistX.under_shutin_count}</span>
              <span style={{ fontFamily:MONO, fontSize:10, color:C.blue }}>↓ Over: {effDistX.over_shutin_count}</span>
            </div>
          )}
        </ChartCard>

      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
        <ChartCard title="Required Shut-In Breakdown: Gas vs Liquid Travel Time"
          subtitle="Gas travel = (D-hl)/Vfg  |  Liquid travel = hl/Vfl"
          accentColor={C.amber} height={280} delay={0.25}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={physBreakdown} margin={CM}>
              <defs>
                <linearGradient id="ggas" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor={C.amber} stopOpacity={0.9}/><stop offset="100%" stopColor={C.amber} stopOpacity={0.3}/>
                </linearGradient>
                <linearGradient id="gliq" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor={C.blue} stopOpacity={0.9}/><stop offset="100%" stopColor={C.blue} stopOpacity={0.3}/>
                </linearGradient>
              </defs>
              <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
              <XAxis dataKey="index" {...CYCLE_XAXIS_PROPS()} height={hasT?20:undefined} label={hasT?undefined:cycLabel}/>
              {dateXAxis(physBreakdown,"index",5,"Cycle")}
              <YAxis tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false} label={{ value:"Minutes", angle:-90, position:"insideLeft", offset:14, fill:C.muted, fontSize:9, fontFamily:MONO }}/>
              <Tooltip content={<TTip />} wrapperStyle={{ zIndex:99999, overflow:"visible !important" }}/>
              <Legend {...LEGEND_PROPS}/>
              <Bar dataKey="gas_travel_min"    name="Gas Travel (min)"    stackId="a" fill="url(#ggas)" radius={[0,0,0,0]}/>
              <Bar dataKey="liquid_travel_min" name="Liquid Travel (min)" stackId="a" fill="url(#gliq)" radius={[3,3,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Liquid Height per Cycle" subtitle="hl = estimated liquid column height"
          accentColor={C.blue} height={280} delay={0.3}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={tsData.filter(d=>d.LiquidHeight_ft!=null)} margin={CM}>
              <defs>
                <linearGradient id="gliqht" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={C.blue} stopOpacity={0.35}/><stop offset="95%" stopColor={C.blue} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
              <XAxis dataKey="index" {...CYCLE_XAXIS_PROPS()} height={hasT?20:undefined} label={hasT?undefined:cycLabel}/>
              {dateXAxis(tsData.filter(d=>d.LiquidHeight_ft!=null),"index",5,"Cycle")}
              <YAxis tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false} label={{ value:"ft", angle:-90, position:"insideLeft", offset:14, fill:C.muted, fontSize:9, fontFamily:MONO }}/>
              <Tooltip content={<TTip />} wrapperStyle={{ zIndex:99999, overflow:"visible !important" }}/>
              <Area type="monotone" dataKey="LiquidHeight_ft" name="Liquid Ht (ft)" stroke={C.blue} fill="url(#gliqht)" strokeWidth={2} dot={false}/>
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <BuildupVsDurationChart rows={buildupScatter} hasT={hasT}/>

      <ShutInReferencePanel rows={tsData} oppRows={boRows} TTip={TTip}/>
    </div>
  );
}